/* ===========================================================
   FACADE theme engine — recoloring room + PDP + AJAX cart
   =========================================================== */
(function () {
  if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  // open each page at the top (once) — no timeout chain that would fight the user's scroll
  window.addEventListener("pageshow", () => window.scrollTo(0, 0));
  // Fix 3rd-party async stylesheets (e.g. Judge.me) that use media="nope!" in Chromium (Chrome/Android/Edge/Brave)
  (function() {
    function unblockNopeStyles() {
      document.querySelectorAll('link[media="nope!"]').forEach((l) => { l.media = "all"; });
    }
    unblockNopeStyles();
    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(unblockNopeStyles).observe(document.head || document.documentElement, { childList: true, subtree: true });
    }
  })();
  const $ = (s, r = document) => r.querySelector(s);

  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const room = $("#room");
  let INV = {};   // variantId → available stock (set on a product page); caps optimistic cart quantities
  let STOCK = {}; // variantId → TRUE inventory count for the display block (null = untracked)
  const IS_ANDROID = /Android/i.test(navigator.userAgent);
  // low-end Androids only: suspend the card's backdrop blur while it is actually MOVING under the finger
  // (restored the moment it settles) — re-blurring a moving region every frame is what makes the drag heavy there
  // low/mid-range Android only (flagships keep true blur mid-drag); merchant can disable it
  // entirely in Theme settings → Performance → "Suspend glass blur while dragging"
  const DRAG_LITE = (!window.FACADE || window.FACADE.dragLite !== false) && IS_ANDROID && (navigator.deviceMemory || 8) <= 6;
  // first room colour is applied instantly (no 1.3s full-screen repaint on load); only later changes glide.
  // Only the no-WebGL fallback needs the variable transitions — when the shader room is live, .room is
  // display:none, and transitioning 15 inherited registered properties on <html> forces a full-document
  // style recalc every frame for 1.3s after EVERY swipe (the main touch-jank source on phones).
  requestAnimationFrame(() => requestAnimationFrame(() => { if (!GRAD) document.documentElement.classList.add("room-anim"); }));

  /* ---------- YTHRAL studio room (WebGL) ---------- */
  // One style, mobile + desktop: the product's own colour, turned into four studio tones, laid out as
  // broad soft fields of light and shade that slowly turn and sway behind the garment. The film grain
  // on top is the approved one, untouched.
  const VERT = "attribute vec2 p; varying vec2 vTextureCoord; void main(){ vTextureCoord = p*0.5+0.5; gl_Position = vec4(p,0.0,1.0); }";
  const FRAG = `precision highp float;
  uniform float uTime; uniform vec2 uResolution; uniform float uGrain; uniform float uSeed; uniform float uMode; uniform float uGSpd; uniform float uGSize;
  uniform float uFluid; uniform vec3 uTrail[8];
  uniform vec3 uTopColor; uniform vec3 uBottomColor; uniform vec3 uAccentColor; uniform vec3 uDarkColor;
  uniform vec2 uFocusPoint; uniform float uFocusStrength;
  varying vec2 vTextureCoord;
  vec2 ghash(vec2 p){ p=vec2(dot(p,vec2(2127.1,81.17)),dot(p,vec2(1269.5,283.37))); return fract(sin(p)*43758.5453)*2.0-1.0; }   /* the same hash the previous room used */
  float gnoise(vec2 p){                              /* smooth gradient noise, 0..1 */
    vec2 i=floor(p); vec2 f=fract(p);
    vec2 u=f*f*(3.0-2.0*f);
    float a=dot(ghash(i),f), b=dot(ghash(i+vec2(1.0,0.0)),f-vec2(1.0,0.0));
    float c=dot(ghash(i+vec2(0.0,1.0)),f-vec2(0.0,1.0)), d=dot(ghash(i+vec2(1.0,1.0)),f-vec2(1.0,1.0));
    return 0.5+0.5*mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
  }
  mat2 rot(float a){ float s=sin(a), c=cos(a); return mat2(c,-s,s,c); }
  void main(){
    vec2 uv=vTextureCoord;
    float ar=uResolution.x/uResolution.y;
    vec2 q=(uv-0.5)*vec2(ar,1.0);
    /* LIQUID TOUCH — the cursor/finger trail swirls the colour field like wet paint;
       the grain stays crisp above it (it samples screen space), only the light bends */
    if(uFluid>0.001){
      vec2 fl=vec2(0.0);
      for(int i=0;i<8;i++){
        vec3 tr=uTrail[i];
        if(tr.z>0.004){
          vec2 dl=q-tr.xy;
          float infl=tr.z*exp(-dot(dl,dl)*34.0);
          fl+=vec2(-dl.y,dl.x)*infl*1.6+dl*infl*0.35;
        }
      }
      q+=fl*uFluid;
    }
    /* the field: the whole room slowly TURNS (angle from a drifting noise, varying across the screen,
       which also swirls it) and SWAYS (two crossed sine waves); then the four tones are laid out as two
       soft side-to-side blends crossed top-to-bottom — broad light and deep shade, never a flat ramp */
    float t=uTime*0.5;
    vec2 p=q/vec2(ar,1.0);
    float turn=(gnoise(vec2(t*0.05,p.x*p.y))-0.5)*12.566+3.1416;
    p.y/=ar; p=p*rot(turn); p.y*=ar;
    float sw=t*2.0;
    p.x+=sin(p.y*5.0+sw)/30.0;
    p.y+=sin(p.x*7.5+sw)/15.0;
    float side=smoothstep(-0.3,0.2,(rot(0.0873)*p).x);
    vec3 shade=mix(uAccentColor,uDarkColor,side);
    vec3 light=mix(uBottomColor,uTopColor,side);
    vec3 col=mix(shade,light,smoothstep(0.5,-0.3,p.y));
    /* optional light that follows the cursor (merchant setting; off by default) */
    vec2 warp=p-(uv-0.5);
    vec2 dd=(uv-0.5)-(uFocusPoint-0.5)+warp*0.5; dd.x*=ar;
    col=mix(col,mix(col,uTopColor,0.45),smoothstep(0.6,0.0,length(dd))*uFocusStrength);
    /* YTHRAL film stock, layer 1: a fine FIXED grain pressed into the field — one value per pixel that
       never moves (10%, evenly light/dark). It is what makes the room read as film rather than video,
       and it gives the moving layer below a dense texture to live in. */
    col=max(col-length(ghash(uv)*0.5+0.5)*0.1,0.0);
    /* YTHRAL film stock, layer 2: each pixel's grain pulses on its own phase (2 pulses a second at the
       default), shaped into a short soft flash and added mostly in the shadows (1-col). Strength 0.05
       and the 2.0 cadence are the cinematic levels; slower or stronger reads as crawling specks.
       Coords stay in 0..1 (sin() precision — large args collapse into a woven pattern). */
    vec2 guv = uGSize>1.001 ? floor(uv*uResolution/uGSize)*(uGSize/uResolution) : uv;
    float gn=fract(sin(dot(guv,vec2(12.9898,78.233)))*43758.5453+uTime*uGSpd);
    gn=1.5957*exp(-8.0*gn*gn);
    col+=vec3(gn)*(1.0-col)*uGrain;
    gl_FragColor=vec4(col,1.0);
  }`;
  function hslArr(h,s,l){ h=(h%1+1)%1; const f=(n,k=(n+h*12)%12)=>l-s*Math.min(l,1-l)*Math.max(-1,Math.min(k-3,Math.min(9-k,1))); return [f(0),f(8),f(4)]; }
  // the room's four tones from ONE colour (pulled automatically from the product photo): a light tint,
  // a deeper shade, the colour itself and a deep dark of the same hue.
  // adjust (desktop hover): a very dark colour is lifted and a very light one is toned down + enriched first.
  // v158 brand room: the four shader tones straight from the theme settings (Room gradient > Brand colour)
  function brandPalette(){
    const B = window.BRAND_ROOM; if (!B || B.length < 4) return null;
    try { return B.map(h => parseRgb255(String(h)).map(v => v / 255)); } catch (e) { return null; }
  }
  function roomPalette(r,g,b,adjust){
    let hs=rgbToHsl(r,g,b), h=hs[0], s=hs[1], l=hs[2];
    if (adjust){
      if (l<0.25) l=Math.max(l,0.12)+0.13;
      if (l>=0.75){ l=0.65+(l-0.75)*0.4; s=Math.min(s*1.3,1); }
    }
    return [ hslArr(h,s*0.85,Math.min(l+0.25,0.78)), hslArr(h,s*0.95,Math.max(l-0.2,0.22)), hslArr(h,s*0.9,l), hslArr(h,s*0.7,0.18) ];  // top, bottom, accent, dark
  }


  const parseRgb255 = (str) => {
    str = str || "";
    if (str[0] === "#"){ let x=str.slice(1); if(x.length===3) x=x.replace(/./g,"$&$&"); const n=parseInt(x,16)||0; return [(n>>16)&255,(n>>8)&255,n&255]; }
    const m=/(\d+)\D+(\d+)\D+(\d+)/.exec(str); return m?[+m[1],+m[2],+m[3]]:[100,100,110];
  };
  function initGradient(){
    const cv=$("#gradient-canvas"); if(!cv) return null;
    let gl; try{ gl=cv.getContext("webgl",{alpha:false,antialias:false,powerPreference:"low-power"})||cv.getContext("experimental-webgl"); }catch(e){}
    if(!gl) return null;
    const sh=(t,s)=>{ const o=gl.createShader(t); gl.shaderSource(o,s); gl.compileShader(o); return gl.getShaderParameter(o,gl.COMPILE_STATUS)?o:null; };
    const vs=sh(gl.VERTEX_SHADER,VERT), fs=sh(gl.FRAGMENT_SHADER,FRAG); if(!vs||!fs) return null;
    const pr=gl.createProgram(); gl.attachShader(pr,vs); gl.attachShader(pr,fs); gl.linkProgram(pr);
    if(!gl.getProgramParameter(pr,gl.LINK_STATUS)) return null;
    gl.useProgram(pr);
    const bf=gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER,bf);
    gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,3,-1,-1,3]),gl.STATIC_DRAW);
    const lp=gl.getAttribLocation(pr,"p"); gl.enableVertexAttribArray(lp); gl.vertexAttribPointer(lp,2,gl.FLOAT,false,0,0);
    const U=n=>gl.getUniformLocation(pr,n);
    const uRes=U("uResolution"),uTime=U("uTime"),uGrain=U("uGrain"),uSeed=U("uSeed"),uFocus=U("uFocusPoint"),uFs=U("uFocusStrength"),uMode=U("uMode"),uFluidL=U("uFluid"),uTrailL=U("uTrail[0]"),uGSpdL=U("uGSpd"),uGSizeL=U("uGSize");
    const GLOWSTR=Math.max(0,Math.min(1,((window.FACADE&&FACADE.roomGlow)!=null?FACADE.roomGlow:0)/100));    // Texture → cursor light strength (0 = none)
    const FLUID=Math.max(0,Math.min(1,((window.FACADE&&FACADE.roomFluid)!=null?FACADE.roomFluid:0)/100));    // Texture → liquid touch strength (0 = off)
    const TRAIL=[]; let lastTrailT=0;
    const trailPush=(cx,cy)=>{ lastTrailT=performance.now();
      TRAIL.push({x:(cx/window.innerWidth-0.5)*(window.innerWidth/window.innerHeight),y:0.5-cy/window.innerHeight,t:lastTrailT});
      if(TRAIL.length>8) TRAIL.shift(); };
    if(FLUID>0){
      window.addEventListener("pointermove",e=>{ if(!e.pointerType||e.pointerType==="mouse") trailPush(e.clientX,e.clientY); },{passive:true});
      window.addEventListener("touchmove",e=>{ trailPush(e.touches[0].clientX,e.touches[0].clientY); },{passive:true});
    }
    const trailBuf=new Float32Array(24);
    const MOTION={orbit:0,lava:1,silk:2,aurora:3,breath:4,ink:5,tide:6,nebula:7,ember:8,pulse:9,rise:10,sweep:11,duel:12,bloom:13,mirage:14,marble:15};
    gl.uniform1f(uMode, MOTION[(window.FACADE&&FACADE.roomMotion)||"orbit"]||0);
    const SPD=Math.max(0.2,Math.min(2.5,((window.FACADE&&FACADE.roomSpeed)||100)/100));   // merchant motion-speed dial
    const uCol=[U("uTopColor"),U("uBottomColor"),U("uAccentColor"),U("uDarkColor")];
    // start DARK (matches the page background) and bloom quickly to the first palette — the old light-blue
    // default painted a wrong-coloured first frame ("room flashes black/blue, then recolours") on every entry.
    // tgt keeps the original default so pages that never set a palette (cart/policies/404) look exactly as before.
    let cur=[[.13,.13,.17],[.10,.10,.14],[.09,.09,.13],[.03,.03,.05]], tgt=[[.45,.45,.8],[.25,.25,.5],[.2,.2,.45],[.04,.04,.1]];
    // product page with a known room colour → the very FIRST shader frame is already that room (no bloom, no flash)
    if (window.ROOM_SEED){ try{ const c=parseRgb255(window.ROOM_SEED); cur=roomPalette(c[0],c[1],c[2]).map(x=>x.slice()); tgt=cur.map(x=>x.slice()); }catch(e){} }
    // the gap just showed the PREVIOUS page's exact last shader frame → the first live frame must
    // be that same room, then bloom to this page's own colour. Zero discontinuity, ever.
    if (window.__fxSnapHex){ try{ const c2=parseRgb255(window.__fxSnapHex); cur=roomPalette(c2[0],c2[1],c2[2]).map(x=>x.slice()); }catch(e){} }
    // v158: brand pages (cart, search, contact, policies, collections, 404) bloom into the brand room
    if (window.BRAND_PAGE && !window.ROOM_SEED){ const bp=brandPalette(); if (bp) tgt=bp; }
    // v196: a first visit without a product colour starts IN the brand room (the colours the page head painted on the
    // canvas before this ran), never from dark. Home on phones then glides to the product in focus as before.
    if (!window.ROOM_SEED && !window.__fxSnapHex){ const bp=brandPalette(); if (bp){ cur=bp.map(x=>x.slice()); tgt=bp.map(x=>x.slice()); } }
    const bodyCS=getComputedStyle(document.body);
    const grain=(parseFloat(bodyCS.getPropertyValue("--grain"))||15)/15*0.05;   // YTHRAL film stock: 15 (default) = 0.05, the cinematic level; the Grain amount slider scales it
    const grainFps=Math.max(0,Math.min(60,parseFloat(bodyCS.getPropertyValue("--grain-fps"))||20));   // merchant slider (Texture): 20 = the cinematic cadence (default), higher = faster, 0 = still
    let fx=0.5, fy=0.4, fStr=0.0, tStr=0.0;
    // spotlight follows the MOUSE only — on touch it spiked on every finger move and kept the shader
    // redrawing (and every frosted surface re-blurring) for seconds after each scroll
    let lastPointer = 0;
    const isMobile = window.matchMedia("(max-width:720px)").matches;
    const isAndroid = /Android/i.test(navigator.userAgent);     // Android Chrome re-blurs a moving background far worse than iOS Safari
    let lastInteract = 0;
    const pingInteract = () => { lastInteract = Math.max(lastInteract, performance.now()); };
    ["wheel","touchmove","scroll","touchstart","touchend"].forEach(ev => window.addEventListener(ev, pingInteract, {passive:true}));
    function size(){                                            // phones render below native res, but high enough that the film grain stays CRISP (chunky upscaled grain read as "ugly clutter")
      const dpr=Math.min(window.devicePixelRatio||1, 2), cap=isMobile?1800:3000;   // density follows the screen's pixels (capped at 2×) — grain specks land near device pixels
      let w=Math.max(2,Math.round(cv.clientWidth*dpr)), h=Math.max(2,Math.round(cv.clientHeight*dpr));
      const mx=Math.max(w,h); if(mx>cap){ const s=cap/mx; w=Math.round(w*s); h=Math.round(h*s); }
      if(cv.width!==w||cv.height!==h){ cv.width=w; cv.height=h; gl.viewport(0,0,w,h); } gl.uniform2f(uRes,w,h);
    }
    window.addEventListener("resize",size,{passive:true}); size();   // size ONCE + on resize (not every frame → no per-frame layout reads)
    // CLOCK CONTINUITY: resume the field's time where the previous page left it — the first
    // frame (including the SYNCHRONOUS one right below) is the SAME pattern the snapshot froze,
    // so motion simply continues. Declared BEFORE first use: the sync draw reads it.
    let clk=0; try{ clk=Math.min(1e5, Math.max(0, parseFloat(sessionStorage.getItem("fxClock"))||0)); if ((performance.getEntriesByType("navigation")[0]||{}).type === "reload") clk=0; }catch(e){}
    // FIRST FRAME, SYNCHRONOUSLY — an alpha:false canvas composites as OPAQUE BLACK from the
    // moment the context exists until its first draw (one rAF later). That black blink sat
    // ABOVE the body, covering every pre-paint tint: the un-killable "3rd colour" flash.
    // Painting the seeded palette in the same synchronous instant means the canvas is never
    // on screen in an undrawn state.
    gl.uniform1f(uTime,clk*SPD); gl.uniform1f(uGrain,grain); gl.uniform1f(uSeed,0.0);
    gl.uniform1f(uGSpdL, 2.0*grainFps/20/SPD);   // YTHRAL film stock: 2.0 pulses per REAL second at the default 20 (slider scales it; the room's motion dial does not)
    const STOCK={ "65":1.0, "35":2.2, "16":3.5, "8":5.5 };   // speck size in render px per film stock (65 = the approved original, untouched)
    gl.uniform1f(uGSizeL, STOCK[String((window.FACADE&&FACADE.grainStock)||"65")]||1.0);
    gl.uniform1f(uFluidL,0.0); gl.uniform3fv(uTrailL,trailBuf);
    gl.uniform2f(uFocus,fx,fy); gl.uniform1f(uFs,0.0);
    for(let i=0;i<4;i++) gl.uniform3f(uCol[i],cur[i][0],cur[i][1],cur[i][2]);
    gl.drawArrays(gl.TRIANGLES,0,3);
    const t0=performance.now()-clk*1000; let raf=0,on=true,last=0;
    const minDelta = isAndroid ? 1000/22 : isMobile ? 1000/30 : 0;   // ORIGINAL thermal profile — long sessions must not heat-throttle into lag
    const TR_MS = 800;                                          // a colour change takes 0.8s, easing out
    let trFrom = cur.map(x => x.slice()), trT0 = performance.now();   // pages that never set a colour still glide to the default
    const settled = () => trT0 < 0;
    let tfx = 0.5, tfy = 0.5;                                   // glow target (point); tStr = glow target strength
    function frame(now){
      if(on) raf=requestAnimationFrame(frame);
      const fluidFresh = FLUID > 0 && (now - lastTrailT) < 700;    // liquid smear needs frames while the hand moves
      const interacting = isMobile && (now - lastInteract) < 180 && !fluidFresh;
      // scrolling/swiping on a phone with NOTHING recolouring → hold the last frame (frosted blur never
      // re-samples a moving background). But while a recolour is in flight, keep drawing at a low rate so
      // the room visibly changes DURING a fast scroll instead of only after the finger stops.
      if(interacting && settled() && isAndroid){ last=now; return; }   // Android: full hold (worst re-blur engine)
      if(now-last < (fluidFresh ? 33 : (interacting ? 100 : minDelta))) return;   // iOS scroll: 10fps cadence — the grain keeps LIVING through the gesture
      const dt = last ? Math.min(now-last, 200) : 16.7;
      last=now;
      // opening bloom is FAST (~0.35s — the page must feel instantly "on"); after that, product-to-product
      // recolours keep the graceful ~1s fade. Time-corrected → identical on a 22fps phone and 144Hz desktop.
      if (trT0 >= 0) {
        const q = Math.min(1, (now - trT0) / TR_MS), e = 1 - Math.pow(1 - q, 5);
        for(let i=0;i<4;i++) for(let j=0;j<3;j++) cur[i][j] = trFrom[i][j] + (tgt[i][j] - trFrom[i][j]) * e;
        if (q >= 1) trT0 = -1;
      }
      const kf = 1 - Math.pow(0.96, dt/16.7);                   // the hover glow and its position glide in/out
      fx += (tfx - fx) * kf; fy += (tfy - fy) * kf; fStr += (tStr - fStr) * kf;
      gl.uniform1f(uTime,(now-t0)/1000*SPD); gl.uniform1f(uGrain,grain);
      gl.uniform1f(uFluidL,FLUID);
      for(let ti=0;ti<8;ti++){ const tr=TRAIL[ti]; const a3=ti*3;
        if(tr){ trailBuf[a3]=tr.x; trailBuf[a3+1]=tr.y; trailBuf[a3+2]=Math.max(0,Math.exp(-(now-tr.t)/620))*1.0; }
        else { trailBuf[a3]=0; trailBuf[a3+1]=0; trailBuf[a3+2]=0; } }
      gl.uniform3fv(uTrailL,trailBuf);
      gl.uniform2f(uFocus,fx,fy); gl.uniform1f(uFs,fStr);
      for(let i=0;i<4;i++) gl.uniform3f(uCol[i],cur[i][0],cur[i][1],cur[i][2]);
      gl.drawArrays(gl.TRIANGLES,0,3);
      if(!frame._tintDone){ frame._tintDone=true; requestAnimationFrame(()=>{ const gp=document.getElementById("fx-prepaint"); if(gp) gp.remove(); }); }   // first frame is ON SCREEN → the pre-paint tint may retire
    }
    raf=requestAnimationFrame(frame);
    document.addEventListener("visibilitychange",()=>{ on=!document.hidden; if(on) raf=requestAnimationFrame(frame); else cancelAnimationFrame(raf); });
    if (document.prerendering) document.addEventListener("prerenderingchange", () => {   // this page was built BEFORE the tap — adopt the colour handed over at activation
      try {
        let c = (sessionStorage.getItem("fxRoom") || "").trim().toLowerCase();
        if (/^#[0-9a-f]{3}$/.test(c)) c = "#" + c[1] + c[1] + c[2] + c[2] + c[3] + c[3];
        if (/^#[0-9a-f]{6}$/.test(c)) { const p = parseRgb255(c); cur = roomPalette(p[0], p[1], p[2]).map(x => x.slice()); tgt = cur.map(x => x.slice()); }
        if (window.BRAND_PAGE) { const bp = brandPalette(); if (bp) tgt = bp; }   // v158: a prerendered brand page still ends in the brand room
      } catch (e) {}
    }, { once: true });
    return { setPalette(c){ trFrom = cur.map(x => x.slice()); tgt = c; trT0 = performance.now(); }, focus(x,y,s){ tfx=x; tfy=y; tStr=s; }, unfocus(){ tfx=0.5; tfy=0.5; tStr=0; },
      hold(){ pingInteract(); },   // phone carousel glide = interaction (same rules as a finger on screen)
      time(){ return (performance.now()-t0)/1000; },
      // last-frame capture for the navigation gap: redraw synchronously (the buffer may already be
      // composited away), downscale to a thumbnail — upscaled by CSS it is indistinguishable from
      // the live field (the .grain overlay supplies the film on top). ~1-2 KB as jpeg.
      snap(){ try{
        // FULL-RESOLUTION, GRAIN INCLUDED — the gap must be the EXACT last frame the eye saw,
        // noise and all (every synthetic gap-grain attempt failed the owner's eye; holding the
        // real pixels cannot). Clock continuity makes the next engine frame resume from this
        // same pattern, so the frozen grain melts straight into the living grain.
        gl.drawArrays(gl.TRIANGLES,0,3);
        const SQ=Math.max(280,Math.min(1120,(window.FACADE&&FACADE.snapQ)||1120));   // Texture → transition frame quality
        let url;
        if (cv.width<=SQ){ url=cv.toDataURL("image/jpeg",0.8); }                      // phones: native res, zero rescale
        else { const sc=document.createElement("canvas"); sc.width=SQ; sc.height=Math.max(48,Math.round(SQ*cv.height/Math.max(1,cv.width)));
               sc.getContext("2d").drawImage(cv,0,0,sc.width,sc.height); url=sc.toDataURL("image/jpeg",0.8); }
        return url; }catch(e){ return null; } } };
  }
  const GRAD = initGradient();
  if (GRAD){ window.GRAD=GRAD; if(room) room.style.display="none"; const gr=$(".grain"); if(gr) gr.style.display="none"; }   // shader carries the film grain; the overlay serves the gap + no-WebGL
  else { const c=$("#gradient-canvas"); if(c) c.style.display="none"; }

  /* ---------- money ---------- */
  function money(cents) {
    // NATIVE MULTI-CURRENCY MODE (Shopify Payments + Markets): format in the visitor's ACTIVE
    // checkout currency — cart data already arrives converted by Shopify; only the symbol and
    // decimals must follow. The custom display converter is force-disabled in this mode.
    if (SHOP.native) {
      const iso = SHOP.currency || "USD";
      const sym = SHOP.cur_symbol || "$";
      const amt = (cents / 100);
      if (iso === "JPY" || iso === "KRW" || iso === "IDR" || iso === "VND" || iso === "HUF")
        return sym + Math.round(amt).toLocaleString("en-US");
      const dec = (iso === "KWD" || iso === "BHD" || iso === "OMR") ? 3 : 2;
      return sym + amt.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    // currency-converter module (international store): every JS-rendered price flows through here,
    // so the drawer, quantity totals and search results convert automatically. Inactive → untouched.
    if (window.YthralCC && window.YthralCC.ready && window.YthralCC.active) return window.YthralCC.convertCents(cents);
    const fmt = (SHOP.money_format || "${{amount}}");
    const amt = (cents / 100);
    const withCommas = amt.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const noDec = Math.round(amt).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amt.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, "."));
  }
  window.cartMoney = money;   // cart page reuses the exact same money format (server prices, never computed)

  /* ---------- recoloring room ---------- */
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const FALLBACK = ["#6b6b70", "#1c1c20", "#a8a8b0", "#0e0e12", "#55606e"];
  const cache = new Map();
  const hexN = (h) => { h = (h || "").replace("#", ""); if (h.length === 3) h = h.split("").map(c => c + c).join(""); return parseInt(h, 16) || 0; };
  const hexToRgb = (h) => { const n = hexN(h); return `rgb(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255})`; };
  const darkenHex = (h, f) => { const n = hexN(h); return `rgb(${clamp(((n >> 16) & 255) * f)},${clamp(((n >> 8) & 255) * f)},${clamp((n & 255) * f)})`; };

  // turn one average colour into the 3 studio tones: vivid bloom, deep shadow, bright hotspot
  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
    let h, s;
    if (mx === mn) { h = s = 0; }
    else {
      const d = mx - mn;
      s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
      h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
      h /= 6;
    }
    return [h, s, l];
  }
  function hsl(h, s, l) {
    h = (h % 1 + 1) % 1;
    const h2 = (p, q, t) => { t = (t + 1) % 1; if (t < 1/6) return p + (q - p) * 6 * t; if (t < 1/2) return q; if (t < 2/3) return p + (q - p) * (2/3 - t) * 6; return p; };
    let r, g, b;
    if (s === 0) r = g = b = l;
    else { const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q; r = h2(p, q, h + 1/3); g = h2(p, q, h); b = h2(p, q, h - 1/3); }
    return `rgb(${clamp(r * 255)},${clamp(g * 255)},${clamp(b * 255)})`;
  }
  // build a controlled, designed palette from the product's dominant hue (never muddy / neon / odd-hued)
  function tones(r, g, b) {
    let [h, s, L] = rgbToHsl(r, g, b);
    let S, accH, baseL = 0.40;
    if (s < 0.10) {                                                     // neutral product → cool premium room whose DEPTH follows the garment (so white ≠ black)
      h = 0.60; S = 0.17; accH = h + 0.06;
      baseL = Math.max(0.16, Math.min(0.44, 0.44 - L * 0.30));          // white garment → deep room (it pops); black garment → lighter cool room (it pops) — and the two clearly differ
    } else { S = Math.min(0.6, Math.max(0.34, s * 1.4)); accH = h + 0.5; } // coloured → rich hue + complementary accent (unchanged)
    accH = (accH % 1 + 1) % 1;
    if (s >= 0.10 && accH > 0.17 && accH < 0.46) accH = 0.52;           // never a yellow-green accent → snap to teal
    const bloom   = hsl(h, S, baseL);                          // --room-1 main glow
    const deep    = hsl(h, Math.min(S, 0.5), baseL * 0.275);   // --room-2 deep shadow (proportional → 0.11 for coloured, unchanged)
    const deepest = hsl(h, Math.min(S, 0.5), baseL * 0.125);   // --room-2d darkest (→ 0.05 for coloured, unchanged)
    const hi      = hsl(h, S * 0.7, Math.min(0.85, baseL + 0.35));   // --room-hi crisp bright glow (→ 0.75 for coloured, unchanged)
    const accent  = hsl(accH, S * 0.7, baseL * 0.75);          // --room-acc complementary second hue (→ 0.30 for coloured, unchanged)
    return [bloom, deep, hi, deepest, accent];
  }
  function applyRoom(near, far, hi, deepest, accent, raw, hover) {
    if (!GRAD) { const gp = document.getElementById("fx-prepaint"); if (gp) gp.remove(); }   // no-WebGL: .room paints right here, tint done. WebGL: the tint must SURVIVE until the canvas has actually drawn (removing it earlier showed 1-2 frames of raw grey body)
    if (GRAD) {
      // shader room is live → .room is display:none and the --room-* vars have NO visible consumer.
      // Setting them would invalidate computed styles for the whole document on every swipe — skip.
      const c = (raw && raw.length === 3) ? raw : parseRgb255(near); GRAD.setPalette(roomPalette(c[0], c[1], c[2], hover));
    } else {
      const r = document.documentElement.style;          // CSS-room fallback (no WebGL) — unchanged
      r.setProperty("--room-1", near);
      r.setProperty("--room-2", far);
      if (hi) r.setProperty("--room-hi", hi);
      if (deepest) r.setProperty("--room-2d", deepest);
      if (accent) r.setProperty("--room-acc", accent);
      if (near.startsWith("rgb(")) r.setProperty("--room-1t", near.replace("rgb(", "rgba(").replace(")", ",0.84)"));
      if (far.startsWith("rgb("))  r.setProperty("--room-2t", far.replace("rgb(", "rgba(").replace(")", ",0.9)"));
    }
    try { const rr = (raw && raw.length === 3) ? raw : null;    // the TRUE room colour — page-to-page handoffs use this, never the darkened bar tint
      if (rr) window.__fxRawHex = "#" + rr.map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join(""); } catch (e) {}
    const tc = document.getElementById("themeColor");           // tint the Safari / in-app browser bars to THIS product's room colour (only lightly deepened, so it clearly reads as the colour — not near-black)
    if (tc) { const m = near.match(/\d+/g); if (m) { const f = 0.8; tc.setAttribute("content", "#" + m.slice(0,3).map(x => Math.max(0x14, Math.round(Math.min(255, +x) * f)).toString(16).padStart(2, "0")).join("")); } }
  }
  // v158: show the brand room (desktop home at rest, brand pages); the no-WebGL room gets the same colours
  function setBrandRoom() {
    const B = window.BRAND_ROOM; if (!B || B.length < 4) return false;
    if (GRAD) { const bp = brandPalette(); if (!bp) return false; GRAD.setPalette(bp); window.__fxRawHex = String(B[1]).toLowerCase(); }
    else applyRoom(hexToRgb(B[0]), hexToRgb(B[1]), hexToRgb(B[0]), hexToRgb(B[3]), hexToRgb(B[2]));
    const tc = document.getElementById("themeColor");
    if (tc) { const n = hexN(B[1]); tc.setAttribute("content", "#" + [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(x => Math.max(0x14, Math.round(x * 0.8)).toString(16).padStart(2, "0")).join("")); }
    return true;
  }
  function setRoomFromHex(hex, hover) {
    const n = parseInt(hex.replace("#", ""), 16);
    const rr = (n >> 16) & 255, gg = (n >> 8) & 255, bb = n & 255;
    const [near, far, hi, deepest, accent] = tones(rr, gg, bb);
    applyRoom(near, far, hi, deepest, accent, [rr, gg, bb], hover);    // the WebGL room follows the TRUE colour
  }

  // each product gets its OWN gradient layout (highlight spot, bloom positions, angle), not just a recolour.
  // Deterministic from a per-product seed so it's stable for that product.
  function hashStr(s) { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return Math.abs(h); }
  function applyPattern(seed) {
    const rnd = (k) => { const x = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return x - Math.floor(x); };
    const r = document.documentElement.style, pct = (a, b, k) => (a + rnd(k) * (b - a)).toFixed(1) + "%";
    r.setProperty("--hx", pct(10, 62, 1));   r.setProperty("--hy", pct(16, 46, 2));   // highlight spot
    r.setProperty("--hsz", pct(78, 108, 3));                                           // highlight size
    r.setProperty("--sx", pct(48, 96, 4));   r.setProperty("--sy", pct(40, 88, 5));    // secondary glow
    r.setProperty("--mx", pct(34, 66, 6));   r.setProperty("--my", pct(26, 54, 7));    // mid field centre
    r.setProperty("--ang", Math.round(110 + rnd(8) * 100) + "deg");                    // base angle
  }

  function extract(src) {
    return new Promise((resolve) => {
      if (cache.has(src)) return resolve(cache.get(src));
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas"); const s = 48; c.width = s; c.height = s;
          const ctx = c.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, s, s);
          const d = ctx.getImageData(0, 0, s, s).data;
          const buckets = new Map(); let an = 0;            // colour histogram → the GARMENT BASE is the biggest area, not the small bright print
          for (let i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 200) continue;                    // transparent background → skip
            const R = d[i], G = d[i + 1], B = d[i + 2];
            an++;
            const key = (R >> 5) * 64 + (G >> 5) * 8 + (B >> 5);   // quantise to 8 levels/channel so a garment's shades group together
            let bk = buckets.get(key);
            if (!bk) { bk = { n: 0, r: 0, g: 0, b: 0 }; buckets.set(key, bk); }
            bk.n++; bk.r += R; bk.g += G; bk.b += B;
          }
          if (!an) { cache.set(src, FALLBACK); return resolve(FALLBACK); }
          let best = null;                                   // most pixels = the garment itself (white shirt → white, black shirt → black, lavender → lavender) — NOT the print
          buckets.forEach(bk => { if (!best || bk.n > best.n) best = bk; });
          const r = best.r / best.n, g = best.g / best.n, b = best.b / best.n;
          const out = tones(r, g, b);
          // the room colour = the garment's own main colour; a DARK garment gets a lighter room in its own
          // hue (black → light grey, navy → light blue), so a dark piece never sinks into its own room
          const gH = rgbToHsl(r, g, b);
          const room = gH[2] >= 0.35 ? [r, g, b] : hsl(gH[0], gH[1], gH[1] < 0.10 ? 0.83 : 0.64).match(/\d+/g).map(Number);
          out.push(room.map(Math.round));   // 6th item = the room colour for the WebGL room
          cache.set(src, out); resolve(out);
        } catch (e) { resolve(FALLBACK); }
      };
      img.onerror = () => resolve(FALLBACK);
      img.src = src;
    });
  }
  async function setRoom({ hex, img, seed, hover }) {
    if (seed != null && !GRAD) applyPattern(hashStr(String(seed)));   // pattern vars feed .room only — skip when the shader room is live
    if (hex) return setRoomFromHex(hex, hover);                  // the product's Color code wins (snippets/room-colour)
    const R = window.ROOM;                                       // merchant's custom palette (theme settings)
    if (R) return applyRoom(hexToRgb(R.mid), hexToRgb(R.deep), hexToRgb(R.hi), darkenHex(R.deep, 0.5), hexToRgb(R.acc));
    if (img) { const [n, f, h, d, a, raw] = await extract(img); applyRoom(n, f, h, d, a, raw, hover); }
  }

  /* ---------- YTHRAL universal room: ONE room for every page, section and template ----------
     Every element that shows a product (home spot, collection tile, collab tile, the product page,
     any future section) is coloured the same way: its data-room — the colour code of the product's
     Color (Shopify admin → Content → Metaobjects → Color), written by snippets/room-colour — wins,
     otherwise the colour is read from its photo. Same shades, motion, glow and fades everywhere; no section keeps a
     private room. Sections call FACADE_ROOM.show(element) when that product takes the focus. */
  const roomSrc = (el) => {
    if (!el) return null;
    if (el.dataset && el.dataset.img) return el.dataset.img;
    const im = el.querySelector && el.querySelector("img");
    return im ? (im.currentSrc || im.src || null) : null;
  };
  window.FACADE_ROOM = {
    show(el, hover) {
      if (!el) return;
      const src = roomSrc(el);
      setRoom({ hex: (el.dataset && el.dataset.room) || null, img: src, seed: src, hover: !!hover });
    },
    prewarm(el) {                                                  // read a neighbour's colour early → its recolour is instant
      if (!el || (el.dataset && el.dataset.room)) return;
      const src = roomSrc(el);
      if (src) extract(src);
    }
  };

  /* ---------- YTHRAL soft focus: a lag-free blurred copy of a photo, for any section ----------
     The blur is painted ONCE into a canvas; showing or hiding it is a plain opacity change, so
     nothing re-blurs while it moves (a live CSS filter: blur re-renders every frame — the iPhone
     lag). Canvas filter where the browser has one, else a 3-box gaussian on premultiplied alpha.
     make(img, sigma) → a canvas covering the photo plus room for the blur to spread, or null. */
  window.FACADE_SOFT = (() => {
    const CF = (() => { try { return "filter" in CanvasRenderingContext2D.prototype; } catch (e) { return false; } })();
    const boxes = (s) => {
      const wI = Math.floor(Math.sqrt(4 * s * s + 1));
      const wl = wI % 2 ? wI : wI - 1, wu = wl + 2;
      const m = Math.round((12 * s * s - 3 * wl * wl - 12 * wl - 9) / (-4 * wl - 4));
      return [0, 1, 2].map((i) => ((i < m ? wl : wu) - 1) / 2);
    };
    const boxH = (a, b, w, h, r) => {
      const ia = 1 / (r + r + 1);
      for (let y = 0; y < h; y++) {
        const o = y * w; let s = 0;
        for (let x = 0; x <= r && x < w; x++) s += a[o + x];
        for (let x = 0; x < w; x++) { b[o + x] = s * ia; if (x + r + 1 < w) s += a[o + x + r + 1]; if (x - r >= 0) s -= a[o + x - r]; }
      }
    };
    const boxV = (a, b, w, h, r) => {
      const ia = 1 / (r + r + 1);
      for (let x = 0; x < w; x++) {
        let s = 0;
        for (let y = 0; y <= r && y < h; y++) s += a[y * w + x];
        for (let y = 0; y < h; y++) { b[y * w + x] = s * ia; if (y + r + 1 < h) s += a[(y + r + 1) * w + x]; if (y - r >= 0) s -= a[(y - r) * w + x]; }
      }
    };
    const blurData = (d, w, h, s) => {
      const n = w * h, ch = [new Float32Array(n), new Float32Array(n), new Float32Array(n), new Float32Array(n)], tmp = new Float32Array(n);
      for (let i = 0; i < n; i++) { const a = d[i * 4 + 3], k = a / 255; ch[0][i] = d[i * 4] * k; ch[1][i] = d[i * 4 + 1] * k; ch[2][i] = d[i * 4 + 2] * k; ch[3][i] = a; }
      const bs = boxes(s);
      ch.forEach((c) => bs.forEach((r) => { if (r >= 1) { boxH(c, tmp, w, h, r); boxV(tmp, c, w, h, r); } }));
      for (let i = 0; i < n; i++) {
        const a = ch[3][i]; d[i * 4 + 3] = a;
        if (a > 0.5) { const k = 255 / a; d[i * 4] = ch[0][i] * k; d[i * 4 + 1] = ch[1][i] * k; d[i * 4 + 2] = ch[2][i] * k; }
        else { d[i * 4] = d[i * 4 + 1] = d[i * 4 + 2] = 0; }
      }
    };
    return {
      make(img, sigma) { const l = this.ladder(img, [sigma]); return l ? l[0] : null; },
      ladder(img, steps) {                                           // canvases for each blur step, ONE draw of the photo
        const sigma = steps[steps.length - 1];
        const W = img.offsetWidth, H = img.offsetHeight;
        if (!(sigma > 0) || !W || !H || !img.complete || !img.naturalWidth) return null;
        const RES = 0.5, pad = Math.ceil(2.2 * sigma);               // half resolution is plenty under a blur
        const cw = Math.max(1, Math.round((W + 2 * pad) * RES)), chh = Math.max(1, Math.round((H + 2 * pad) * RES));
        const base = document.createElement("canvas");
        base.width = cw; base.height = chh;
        const bx = base.getContext("2d", CF ? undefined : { willReadFrequently: true });
        bx.imageSmoothingEnabled = true; bx.imageSmoothingQuality = "high";
        const rad = parseFloat(getComputedStyle(img).borderTopLeftRadius) || 0;   // keep the photo's rounded corners
        if (rad > 0 && bx.roundRect) { bx.beginPath(); bx.roundRect(pad * RES, pad * RES, W * RES, H * RES, rad * RES); bx.clip(); }
        bx.drawImage(img, pad * RES, pad * RES, W * RES, H * RES);
        const src = CF ? null : bx.getImageData(0, 0, cw, chh);
        return steps.map((s) => {
          const c = document.createElement("canvas");
          c.width = cw; c.height = chh; c.className = "fx-soft"; c.setAttribute("aria-hidden", "true");
          c.style.cssText = "position:absolute;left:" + -pad + "px;top:" + -pad + "px;width:calc(100% + " + 2 * pad + "px);height:calc(100% + " + 2 * pad + "px);pointer-events:none";
          const cx = c.getContext("2d");
          if (CF) { cx.filter = "blur(" + s * RES + "px)"; cx.drawImage(base, 0, 0); }
          else { const id = new ImageData(new Uint8ClampedArray(src.data), cw, chh); blurData(id.data, cw, chh, s * RES); cx.putImageData(id, 0, 0); }
          return c;
        });
      }
    };
  })();

  /* ---------- header ---------- */
  const navToggle = $("#navToggle");
  if (navToggle) navToggle.addEventListener("click", () => navToggle.closest(".topbar").classList.toggle("nav-open"));
  $$(".nav-parent").forEach(b => b.addEventListener("click", () => b.setAttribute("aria-expanded", b.closest(".nav-group").classList.toggle("open"))));
  // tap anywhere outside the open menu just CLOSES it — and swallows that tap so it doesn't also open a product/link
  document.addEventListener("click", (e) => {
    const bar = $(".topbar");
    if (!bar || !bar.classList.contains("nav-open")) return;
    if (e.target.closest("#navToggle") || e.target.closest(".nav-drop") || e.target.closest(".nav-inline")) return;
    e.preventDefault(); e.stopPropagation();
    bar.classList.remove("nav-open");
  }, true);   // capture → runs before the product's own click handler

  // search — a frosted-glass panel under the header (like the cart); the page fades behind it.
  // Opens on the current page; only navigates when you pick a result (or press Search on the keyboard).
  (function(){
    const ovl = $("#searchOvl"); if (!ovl) return;
    const field = $("#searchField"), results = $("#searchResults");
    const html = document.documentElement, vv = window.visualViewport;
    let shut = 0;
    // the iPhone keyboard can shift and shrink the visible area: keep the panel under the header and the
    // result list above the keyboard
    const fit = () => {
      if (!vv || ovl.hidden) return;
      ovl.style.setProperty("--vv-top", Math.max(0, Math.round(vv.offsetTop)) + "px");
      results.style.maxHeight = Math.max(120, Math.round(vv.offsetTop + vv.height - results.getBoundingClientRect().top - 24)) + "px";
    };
    const open = () => {
      clearTimeout(shut);
      ovl.hidden = false; void ovl.offsetWidth;                      // start state first, so the drop-in animates
      ovl.classList.add("open"); html.classList.add("search-open");
      if (field) { try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); } }   // inside the tap → the keyboard opens
      fit();
      if (vv) { vv.addEventListener("resize", fit); vv.addEventListener("scroll", fit); }
    };
    const close = () => {
      ovl.classList.remove("open"); html.classList.remove("search-open");
      if (field) field.blur();
      if (vv) { vv.removeEventListener("resize", fit); vv.removeEventListener("scroll", fit); }
      clearTimeout(shut);
      shut = setTimeout(() => { ovl.hidden = true; ovl.style.removeProperty("--vv-top"); results.style.maxHeight = ""; if (field) field.value = ""; results.innerHTML = ""; }, 360);
    };
    $$("[data-search-toggle]").forEach(b => b.addEventListener("click", open));
    const sc = $("#searchClose"); if (sc) sc.addEventListener("click", close);
    ovl.addEventListener("click", (e) => { if (e.target === ovl) close(); });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !ovl.hidden) close(); });
    let t;
    if (field) field.addEventListener("input", () => {
      clearTimeout(t);
      const q = field.value.trim();
      if (q.length < 2) { results.innerHTML = ""; return; }
      t = setTimeout(async () => {
        try {
          const base = (SHOP.routes && SHOP.routes.search) || "/search";
          const data = await fetch(`${base}/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`).then(r => r.json());
          if (!field || field.value.trim() !== q) return;                 // a newer query is already on its way
          const items = (((data.resources || {}).results || {}).products) || [];
          if (!items.length) { results.innerHTML = `<div class="search-empty">No matches for “${escH(q)}”</div>`; fit(); return; }
          results.innerHTML = items.map(p => {
            let img = (p.featured_image && p.featured_image.url) || p.image || "";
            img = img.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, "_180x$1$2");   // 42px thumb — never download the full-res master
            // suggest.json returns a bare number ("2499.00") — format it like every other price on the site
            const price = p.price != null && /^\d+(\.\d+)?$/.test(String(p.price)) ? money(Math.round(parseFloat(p.price) * 100)) : (p.price || "");
            return `<a class="search-item" href="${p.url}"><img src="${img}" alt="" width="56" height="50"><span class="si-t">${escH(p.title)}</span>${price ? `<span class="si-p">${price}</span>` : ""}</a>`;
          }).join("") + `<a class="search-all" href="${base}?q=${encodeURIComponent(q)}&type=product">[ View all results ]</a>`;
          fit();
        } catch (e) { results.innerHTML = ""; }
      }, 220);
    });
  })();

  // desktop: scroll the long inline nav horizontally with the mouse wheel
  const navInline = $(".nav-inline");
  if (navInline) navInline.addEventListener("wheel", (e) => {
    if (!e.deltaY) return;
    e.preventDefault();
    navInline.scrollLeft += e.deltaY;
  }, { passive: false });

  /* ============================================================
     PAGE SCOPE — everything from here to the end of the PDP block
     runs once per PAGE (cold load, and again after the router swaps
     #MainContent). Window/document-level listeners register with the
     page's AbortSignal so a swap cleanly retires them; element-level
     listeners die with the swapped content naturally.
     ============================================================ */
  let PAGE_CTL = null;
  function teardownPage() {
    if (PAGE_CTL) { try { PAGE_CTL.abort(); } catch (e) {} PAGE_CTL = null; }
    // PDP re-parents these onto <body> so they float above the rising cards — remove strays on leave
    [".pdp-info", "#addForm", "#sizeGuide"].forEach((sel) => {
      $$(sel).forEach((el) => { if (el.parentElement === document.body) el.remove(); });
    });
    document.documentElement.classList.remove("sg-open");
    closeCart();
    window.pdpSheets = window.pdpRevealTo = window.showImage = window.syncToImage = undefined;
  }
  function initPage() {
  PAGE_CTL = ("AbortController" in window) ? new AbortController() : null;
  const sig = PAGE_CTL ? PAGE_CTL.signal : undefined;

  /* ---------- home scatter ---------- */
  const scatter = $("#scatter");
  if (scatter && !scatter.dataset.init) {
    scatter.dataset.init = "1";
    const items = $$(".product", scatter);
    // v158: where the room rests when no product is hovered: the brand room on brand pages and on the desktop
    // home (a mouse that can hover); phones keep following the product in focus.
    const brandRest = () => !!window.BRAND_ROOM && (window.BRAND_PAGE || window.matchMedia("(min-width:721px) and (hover:hover)").matches);
    // POSITIONING IS NOT DONE HERE. The home-scatter section owns layout completely
    // (server-rendered inline styles + its own engine). Writing positions from two
    // places raced after router swaps and reset organic mode back to the grid.
    items.forEach((el) => {
      const desk = window.matchMedia("(min-width:721px) and (hover:hover)").matches;
      const glow = (e) => { if (desk && window.GRAD && GRAD.focus) GRAD.focus(e.clientX / window.innerWidth, 1 - e.clientY / window.innerHeight, 1); };
      el.addEventListener("mouseenter", (e) => {
        setRoom({ hex: el.dataset.room, img: el.dataset.img, seed: el.dataset.img, hover: desk });
        glow(e);
      });
      if (desk) {
        el.addEventListener("mousemove", glow);
        el.addEventListener("mouseleave", () => {
          if (window.GRAD && GRAD.unfocus) GRAD.unfocus();
          if (brandRest() && setBrandRoom()) return;          // v158: back to the brand room
          if (items[0]) setRoom({ hex: items[0].dataset.room, img: items[0].dataset.img, seed: items[0].dataset.img });
        });
      }
    });
    // cursor-follow parallax: products drift with the mouse, each at its own
    // depth (--pdep), eased with the same lerp as everything else. Merchant slider = strength.
    const PAR = parseFloat(scatter.dataset.parallax) || 0;
    if (PAR > 0 && window.matchMedia("(min-width:721px)").matches && window.matchMedia("(hover:hover)").matches) {
      let tx = 0, ty = 0, cx = 0, cy = 0, pRaf = 0, pLast = 0;
      // v162: a floaty follow the founder can feel: half the way in about 0.33s, 90% in about 1.1s
      // (3.5% per 60Hz frame), time-based so 60Hz, 120Hz and 144Hz screens feel the same
      const pStep = (now) => {
        const dt = pLast ? Math.min(64, now - pLast) : 16.7; pLast = now;
        const k = 1 - Math.pow(1 - 0.035, dt / 16.7);
        cx += (tx - cx) * k; cy += (ty - cy) * k;
        scatter.style.setProperty("--par-x", cx.toFixed(1) + "px");
        scatter.style.setProperty("--par-y", cy.toFixed(1) + "px");
        if (Math.abs(tx - cx) > 0.15 || Math.abs(ty - cy) > 0.15) pRaf = requestAnimationFrame(pStep);
        else { pRaf = 0; pLast = 0; }
      };
      window.addEventListener("pointermove", (e) => {
        if (e.pointerType && e.pointerType !== "mouse") return;
        tx = (e.clientX / window.innerWidth - 0.5) * PAR;
        ty = (e.clientY / window.innerHeight - 0.5) * PAR * 0.6;
        if (!pRaf) pRaf = requestAnimationFrame(pStep);
      }, { passive: true, signal: sig });
    }
    // pre-warm room colors when idle. Desktop scatters many products at once, so warm them all.
    // Phones show ONE at a time (carousel) → only warm the visible ± neighbours; the rest extract on demand. Saves load-time CPU + bandwidth.
    const warmList = window.matchMedia("(max-width:720px)").matches ? items.slice(0, 3) : items;
    const prewarm = () => warmList.forEach(el => { if (!el.dataset.room) extract(el.dataset.img); });
    if ("requestIdleCallback" in window) requestIdleCallback(prewarm, { timeout: 2500 });
    else setTimeout(prewarm, 1500);

    if (!(brandRest() && setBrandRoom()) && items[0]) setRoom({ hex: items[0].dataset.room, img: items[0].dataset.img, seed: items[0].dataset.img });
    // remember home's OWN opening colour — links back to home hand THIS across, so the homepage
    // never opens in the previous product's colour (the "hard pink flash")
    if (document.documentElement.classList.contains("tpl-index"))
      setTimeout(() => { try { if (window.__fxRawHex) sessionStorage.setItem("fxHomeSeed", window.__fxRawHex); } catch (e) {} }, 1200);

    // mobile: MAGNETIC FOCUS carousel (v106) — attraction physics + depth (scale/blur) neighbours.
    // See the block comment inside for the lag-free construction (no live CSS filters).
    if (window.matchMedia("(max-width:720px)").matches) {
      const n = items.length;
      if (n > 1) {
        const E = items, t = scatter, Bt = setRoom, p = n;
        // YTHRAL MAGNETIC FOCUS CAROUSEL (phones). Physics = fixed 60Hz attraction integrator
        // (velocity += gap / duration; velocity *= friction) with interpolated rendering,
        // so 60Hz and 120Hz phones land identically. Depth = distance-driven scale + blur.
        // LAG-FREE BY CONSTRUCTION: per frame only transform/opacity on composited layers.
        // Blur is NEVER a CSS filter (not live, not static): each garment gets two pre-blurred
        // copies, built once while nothing moves, and the swipe crossfades sharp -> soft -> blurred.
        // The photo's drop shadow is pre-drawn the same way (a CSS shadow is re-rendered by Chrome on
        // every frame of a swipe, and rasterised again by Safari each time a garment first shows).
        // A garment whose copies are not ready yet is never shown sharp where it should look
        // blurred: it stays hidden, then fades in already blurred.
        // Two render paths, same look:
        //  - iOS WebKit: plus-lighter additive crossfade, per-layer scale.
        //  - Chromium/Android: NO blend modes (no offscreen render surfaces): blurrier copies sit
        //    UNDER the sharper one and plain opacity crossfades; ONE transform write
        //    (translate+scale) per garment per frame.
        const root = document.documentElement, UA = navigator.userAgent;
        const WK = /AppleWebKit/.test(UA) && !/Chrome|Chromium|Android|CrOS|Edg/.test(UA);
        const PLUS = WK && !!(window.CSS && CSS.supports && CSS.supports("mix-blend-mode", "plus-lighter"));
        const CSC = !WK;                                   // container scale (one write per garment)
        const BGH = /Android/i.test(UA) && window.GRAD && GRAD.hold ? () => GRAD.hold() : null;
        root.classList.add(PLUS ? "fx-plus" : "fx-layer");
        if (CSC) root.classList.add("fx-cs");
        const GAP = 0.47;                                    // garment spacing as a fraction of the screen height (lower = neighbours peek more)
        const RES = 0.5, LV = [0, 1.2, 2.5, 4, 5.8, 7.8], NL = LV.length - 1, BMAX = LV[NL], STEP = 1000 / 60;   // YTHRAL soft focus ladder: 0 = the sharp photo, then 5 pre-blurred steps close enough that blending two neighbours reads as ONE even blur (never a sharp photo over a glow). BMAX = the resting neighbour's blur, so a resting neighbour is ONE layer
        if (!document.getElementById("fx-mcar-css")) {
          const st = document.createElement("style");
          st.id = "fx-mcar-css";
          st.textContent = ".scatter .product img{will-change:transform,filter}" +
            ".fxbl{position:absolute;max-width:none;pointer-events:none;opacity:0;will-change:transform,opacity}" +
            "html.fx-plus .scatter .phover{isolation:isolate}html.fx-plus .scatter .product img,html.fx-plus .fxbl{mix-blend-mode:plus-lighter}" +
            "html.fx-cs .scatter .product{will-change:transform;transform-origin:calc(50% + var(--prod-x,0px)) calc(50% + var(--prod-y,0px))}";
          document.head.appendChild(st);
        }
        let SP = window.innerHeight * GAP;
        let loc = 0, prv = 0, tgt = 0, vel = 0, dur = 24, fric = 0.68, curOff = 0;
        let sel = 0, raf = 0, running = false, moving = false, lastTs = 0, acc = 0, down = false, focusIdx = -1;   // moving = the carousel itself is in motion (not just a fade)
        E.forEach((el) => { el.style.transition = "none"; el._b = 0; el._h = -1; el._v = 1; });
        const mod = (k) => ((k % p) + p) % p;
        const wrap = (d) => { d = mod(d); return d >= p / 2 ? d - p : d; };
        const predecode = (i) => {
          [i + 1, i - 1, i + 2].forEach((k) => {
            const im = E[mod(k)].querySelector("img");
            if (im && im.decode) im.decode().catch(() => {});
          });
        };
        const seek = () => {
          prv = loc;
          const gap = tgt - loc;
          if (!dur) { vel = 0; loc = tgt; } else { vel += gap / dur; vel *= fric; loc += vel; }
        };
        const setO = (n, v) => { v = Math.round(v * 1000) / 1000; if (n._o !== v) { n._o = v; n.style.opacity = v; } };
        const setT = (n, s) => { if (n._t !== s) { n._t = s; n.style.transform = s; } };
        const layers = (el, sc, b) => {
          const im = el._im || (el._im = el.querySelector("img"));
          let ss = "";
          if (!CSC) { ss = "translateZ(0) scale(" + sc.toFixed(4) + ")"; if (im) setT(im, ss); }
          const bl = el._lost ? null : el._bl;
          if (!bl) {
            if (im) { setO(im, el._bx || b <= 1 ? 1 : b >= 3 ? 0 : (3 - b) / 2); if (im._nf) { im._nf = 0; im.style.filter = ""; } }
            if (el._bl) el._bl.forEach((c) => setO(c, 0));
            return;
          }
          const sl = bl[NL];                               // the pre-drawn shadow travels with the sharp photo
          if (im && sl && !im._nf) { im._nf = 1; im.style.filter = "none"; }
          const u = b > BMAX ? BMAX : b < 0 ? 0 : b;
          let k = 0;
          while (k < NL - 1 && u > LV[k + 1]) k++;         // the two ladder steps around this blur
          const f = (u - LV[k]) / (LV[k + 1] - LV[k]);
          // PLUS (additive): an exact blend of the two steps. Layered: the sharper step on top fades out
          // over the blurrier one, which comes in fast underneath (never a see-through gap)
          const wa = 1 - f, wb = PLUS ? f : (f * 4 > 1 ? 1 : f * 4);
          const w0 = k === 0 ? wa : 0;
          if (im) setO(im, w0);
          for (let j = 1; j <= NL; j++) {
            const c = bl[j - 1], w = j === k ? wa : j === k + 1 ? wb : 0;
            if (w > 0 && !CSC) setT(c, ss);                // only the steps on screen take the per-frame scale
            setO(c, w);
          }
          if (sl) { if (w0 > 0 && !CSC) setT(sl, ss); setO(sl, w0); }
        };
        const paint = (off, dt, instant) => {
          curOff = off;
          let busy = false;
          for (let i = 0; i < p; i++) {
            const el = E[i], d = wrap(i + off / SP), a = Math.abs(d);
            if (a > 1.7) {
              if (el._h !== 1) { el._h = 1; setO(el, 0); setT(el, "translate3d(0,200vh,0)"); }
              continue;
            }
            el._h = 0;
            if (el._v < 1) { el._v = Math.min(1, el._v + dt / 220); busy = true; }   // copies just landed: fade in, already blurred
            setO(el, el._v);
            const l = 1 - 0.52 * a;
            const sc = l < 0.5 ? 0.5 : l > 1 ? 1 : l;
            setT(el, "translate3d(0," + (d * SP).toFixed(2) + "px,0)" + (CSC ? " scale(" + sc.toFixed(4) + ")" : ""));
            let bt = (1 - l) * 15;
            bt = bt < 0 ? 0 : bt > 10 ? 10 : bt;
            el._b = instant ? bt : el._b + (bt - el._b) * Math.min(1, dt / 100);
            if (Math.abs(bt - el._b) > 0.02) busy = true;
            layers(el, sc, el._b);
          }
          return busy;
        };
        const frame = (ts) => {
          if (!lastTs) { lastTs = ts; seek(); seek(); }
          let dt = ts - lastTs;
          lastTs = ts;
          if (dt > 100) dt = 100;
          acc += dt;
          while (acc >= STEP) { seek(); acc -= STEP; }
          const al = acc / STEP;
          let off = loc * al + prv * (1 - al);
          const settled = !down && Math.abs(tgt - off) < 0.01;
          if (settled) { loc = prv = off = tgt; vel = 0; }
          moving = !settled;
          if (BGH && Math.abs(tgt - off) > 0.5) BGH();      // Android: background keeps its touch-time rate while garments visibly glide
          const busy = paint(off, dt, false);
          if (settled && !busy) { running = false; lastTs = 0; acc = 0; kick(); return; }
          if (settled && moving === false) kick();   // only a fade left: builds may go on
          raf = requestAnimationFrame(frame);
        };
        const run = () => { if (!running) { running = true; lastTs = 0; acc = 0; raf = requestAnimationFrame(frame); } };
        const commitFocus = (at) => {
          const near = mod(at != null ? at : sel);
          if (near === focusIdx) return;
          focusIdx = near;
          E.forEach((it, k) => it.classList.toggle("active", k === near));
          const el = E[near];
          Bt({ hex: el.dataset.room, img: el.dataset.img, seed: el.dataset.img });
          predecode(near);
          [2, -2, 1, -1].forEach((k) => ensure(E[mod(near + k)], true));   // ±1 end up at the very front
        };
        // release: YTHRAL flick rules — small flick snaps to the nearest garment, a real flick
        // always moves at least one, a flick after dragging past halfway may carry several
        const release = (raw) => {
          const near = Math.round(-tgt / SP), changed = near !== sel;
          const thr = Math.min(225, Math.max(50, window.innerHeight * 0.2));
          const snapD = (dd) => -Math.round(-(tgt + dd) / SP) * SP - tgt;
          let al;
          if (Math.abs(raw) < thr) al = snapD(raw);
          else if (changed) al = snapD(raw) * 0.5;
          else al = -(sel - Math.sign(raw)) * SP - tgt;
          const ar = Math.abs(raw), aa = Math.abs(al);
          const ff = ar && aa && ar > aa ? (ar - aa) / ar : 0;
          dur = 25 - 10 * ff;
          fric = 0.68 + ff / 50;
          tgt = -Math.round(-(tgt + al) / SP) * SP;
          sel = Math.round(-tgt / SP);
          moving = true;
          commitFocus(sel);
          run();
        };
        let sy = 0, sx = 0, ly = 0, eng = false, tS = null, tL = null, mv = 0, caught = false, noClick = 0;
        t.addEventListener("touchstart", (e) => {
          const tt = e.touches[0];
          sy = ly = tt.clientY; sx = tt.clientX; eng = false; mv = 0; noClick = 0;
          caught = running && Math.abs(tgt - curOff) > 2;     // finger landed on a visibly gliding carousel: a catch, not a tap
          tS = tL = { y: tt.clientY, t: e.timeStamp };
          down = true;
          tgt = loc = prv = curOff; vel = 0; dur = 0; fric = 0;   // grab: a moving garment stops under the finger
        }, { passive: true });
        t.addEventListener("touchmove", (e) => {
          if (!down) return;
          const tt = e.touches[0], y = tt.clientY, x = tt.clientX, dy = y - sy, dx = x - sx;
          const m = Math.max(Math.abs(dy), Math.abs(dx));
          if (m > mv) mv = m;
          if (!eng) {
            // direction is decided on real travel (6px), not on the first 1-2px wobble — a slightly
            // sideways first wobble used to drop the whole swipe and the browser read it as a tap
            if (Math.abs(dy) < 6 || Math.abs(dx) > Math.abs(dy)) return;
            eng = true;
          }
          const now = { y: y, t: e.timeStamp };
          if (now.t - tS.t > 170) tS = now;
          tL = now;
          tgt += y - ly; ly = y;
          dur = 0.75; fric = 0.3; moving = true;
          run();
        }, { passive: true });
        const up = (e) => {
          if (!down) return;
          down = false;
          // Android holds back the first few px of a touch (its tap slop): a quick short flick can arrive
          // as touchstart → touchend with no move at all. The lift point still tells the real travel.
          const ct = e.changedTouches && e.changedTouches[0];
          if (ct) {
            const dy = ct.clientY - sy, dx = ct.clientX - sx, m = Math.max(Math.abs(dy), Math.abs(dx));
            if (m > mv) mv = m;
            if (!eng && Math.abs(dy) >= 6 && Math.abs(dy) >= Math.abs(dx)) {   // a flick the page never saw: hand it to the swipe engine
              eng = true; tL = { y: ct.clientY, t: e.timeStamp }; tgt += ct.clientY - ly; ly = ct.clientY;
            }
          }
          if (eng || caught || mv >= 6) noClick = performance.now() + 600;   // a swipe or a catch never opens a product
          let v = 0;
          if (eng && tS && tL) {
            const h = tL.y - tS.y, pt = e.timeStamp - tS.t, stale = e.timeStamp - tL.t > 170, vv = pt ? h / pt : 0;
            v = pt && !stale && Math.abs(vv) > 0.1 ? vv : 0;
          }
          release(v * 400);
        };
        t.addEventListener("touchend", up, { passive: true });
        t.addEventListener("touchcancel", up, { passive: true });
        // the click the browser may still fire after a short swipe is swallowed here — at WINDOW capture,
        // the very first stop of a click, ahead of every page-transition listener (facade-extras.js
        // listens at document capture). Only carousel clicks right after a swipe/catch; a still tap opens.
        window.addEventListener("click", (e) => {
          if (noClick && performance.now() < noClick && t.contains(e.target)) { e.preventDefault(); e.stopPropagation(); }
        }, true);
        let wT = 0;
        t.addEventListener("wheel", (e) => {
          e.preventDefault();
          tgt -= e.deltaY; dur = 0.75; fric = 0.3; moving = true;
          run();
          clearTimeout(wT);
          wT = setTimeout(() => release(0), 140);
        }, { passive: false });

        // ---- pre-blurred copies (built once per garment, never during a swipe) ----
        const CF = (() => { try { return "filter" in CanvasRenderingContext2D.prototype; } catch (e) { return false; } })();
        const shadowOf = (im) => {
          const m = (getComputedStyle(im).filter || "").match(/drop-shadow\((rgba?\([^)]*\)|#[0-9a-fA-F]{3,8}|[a-z]+)\s+(-?[\d.]+)px\s+(-?[\d.]+)px\s+([\d.]+)px/);
          return m ? { c: m[1], x: +m[2], y: +m[3], b: +m[4] } : null;
        };
        const boxes = (s) => {
          const wI = Math.floor(Math.sqrt(4 * s * s + 1));
          const wl = wI % 2 ? wI : wI - 1, wu = wl + 2;
          const m = Math.round((12 * s * s - 3 * wl * wl - 12 * wl - 9) / (-4 * wl - 4));
          return [0, 1, 2].map((i) => ((i < m ? wl : wu) - 1) / 2);
        };
        const boxH = (src, dst, w, h, r) => {
          const ia = 1 / (r + r + 1);
          for (let y = 0; y < h; y++) {
            const o = y * w; let s = 0;
            for (let x = 0; x <= r && x < w; x++) s += src[o + x];
            for (let x = 0; x < w; x++) {
              dst[o + x] = s * ia;
              if (x + r + 1 < w) s += src[o + x + r + 1];
              if (x - r >= 0) s -= src[o + x - r];
            }
          }
        };
        const boxV = (src, dst, w, h, r) => {
          const ia = 1 / (r + r + 1);
          for (let x = 0; x < w; x++) {
            let s = 0;
            for (let y = 0; y <= r && y < h; y++) s += src[y * w + x];
            for (let y = 0; y < h; y++) {
              dst[y * w + x] = s * ia;
              if (y + r + 1 < h) s += src[(y + r + 1) * w + x];
              if (y - r >= 0) s -= src[(y - r) * w + x];
            }
          }
        };
        let SB = null;                                     // scratch buffers shared by every build: no garbage → no
        const scratch = (n) => {                           // memory clean-up pauses landing in a later swipe
          if (!SB || SB.n < n) SB = { n: n, ch: [new Float32Array(n), new Float32Array(n), new Float32Array(n), new Float32Array(n)], tmp: new Float32Array(n) };
          return SB;
        };
        const blurData = (d, w, h, s) => {
          const n = w * h, sb = scratch(n), ch = sb.ch, tmp = sb.tmp;
          for (let i = 0; i < n; i++) {
            const a = d[i * 4 + 3], k = a / 255;
            ch[0][i] = d[i * 4] * k; ch[1][i] = d[i * 4 + 1] * k; ch[2][i] = d[i * 4 + 2] * k; ch[3][i] = a;
          }
          const bs = boxes(s);
          ch.forEach((c) => bs.forEach((r) => { if (r >= 1) { boxH(c, tmp, w, h, r); boxV(tmp, c, w, h, r); } }));
          for (let i = 0; i < n; i++) {
            const a = ch[3][i];
            d[i * 4 + 3] = a;
            if (a > 0.5) { const k = 255 / a; d[i * 4] = ch[0][i] * k; d[i * 4 + 1] = ch[1][i] * k; d[i * 4 + 2] = ch[2][i] * k; }
            else { d[i * 4] = d[i * 4 + 1] = d[i * 4 + 2] = 0; }
          }
        };
        const build = (el, force) => {
          if ((el._bl && !el._lost && !force) || el._bx) return true;
          const im = el._im || (el._im = el.querySelector("img")), host = el.querySelector(".phover");
          if (!im || !host) { el._bx = 1; return true; }
          if (!im.complete || !im.naturalWidth) return false;
          const W = im.offsetWidth, H = im.offsetHeight;   // layout box (transforms don't change it)
          if (!W || !H) return false;
          try {
            // borders sized to what is actually drawn: baked shadow (to 2.2σ) + the widest blur (to 2.2σ)
            const sh = el._sh !== undefined ? el._sh : (el._sh = shadowOf(im)), eb = 2.2 * BMAX;   // read once: later the photo's own CSS shadow is switched off
            const es = sh ? 1.1 * sh.b : 0, sx0 = sh ? sh.x : 0, sy0 = sh ? sh.y : 0;
            const pL = Math.ceil(Math.max(0, es - sx0) + eb), pR = Math.ceil(Math.max(0, es + sx0) + eb);
            const pT = Math.ceil(Math.max(0, es - sy0) + eb), pB = Math.ceil(Math.max(0, es + sy0) + eb);
            const cw = Math.round((W + pL + pR) * RES), chh = Math.round((H + pT + pB) * RES);
            const base = document.createElement("canvas");
            base.width = cw; base.height = chh;
            const bx = base.getContext("2d", CF ? undefined : { willReadFrequently: true });
            bx.imageSmoothingEnabled = true; bx.imageSmoothingQuality = "high";   // the photo is up to 1000px: downscale cleanly
            if (sh) { bx.shadowColor = sh.c; bx.shadowOffsetX = sh.x * RES; bx.shadowOffsetY = sh.y * RES; bx.shadowBlur = sh.b * RES; }
            bx.drawImage(im, pL * RES, pT * RES, W * RES, H * RES);
            let src = null, idb = null;
            const css = "left:-" + pL + "px;top:-" + pT + "px;width:calc(100% + " + (pL + pR) + "px);height:calc(100% + " + (pT + pB) + "px);transform-origin:" + (pL + W / 2) + "px " + (pT + H / 2) + "px";
            const out = LV.slice(1).map((s) => {
              const c = document.createElement("canvas");
              c.width = cw; c.height = chh; c.className = "fxbl"; c.setAttribute("aria-hidden", "true");
              c.style.cssText = css;
              const cx = c.getContext("2d");
              if (CF) { cx.filter = "blur(" + s * RES + "px)"; cx.drawImage(base, 0, 0); }
              else {
                if (!src) { src = bx.getImageData(0, 0, cw, chh); idb = new ImageData(cw, chh); }
                idb.data.set(src.data);                    // one working copy, reused for every ladder step
                blurData(idb.data, cw, chh, s * RES);
                cx.putImageData(idb, 0, 0);
              }
              // the browser may drop canvas memory (app sent to background): hide it, then rebuild
              c.addEventListener("contextlost", () => { if (el._bl === out) { el._lost = 1; show(); } });
              c.addEventListener("contextrestored", () => { if (el._bl === out) later(() => { if (build(el, true)) show(); }, true); });
              return c;
            });
            if (sh) {
              // the photo's shadow alone: the photo is drawn off-canvas so only its shadow lands here,
              // then the garment's own area is knocked out (the photo itself covers it)
              const c = document.createElement("canvas");
              c.width = cw; c.height = chh; c.className = "fxbl"; c.setAttribute("aria-hidden", "true");
              c.style.cssText = css;
              const sx = c.getContext("2d"), OFF = cw + 64;
              sx.imageSmoothingEnabled = true; sx.imageSmoothingQuality = "high";
              sx.shadowColor = sh.c; sx.shadowOffsetX = sh.x * RES + OFF; sx.shadowOffsetY = sh.y * RES; sx.shadowBlur = sh.b * RES;
              sx.drawImage(im, pL * RES - OFF, pT * RES, W * RES, H * RES);
              sx.shadowColor = "rgba(0,0,0,0)";
              sx.globalCompositeOperation = "destination-out";
              sx.drawImage(im, pL * RES, pT * RES, W * RES, H * RES);
              c.addEventListener("contextlost", () => { if (el._bl === out) { el._lost = 1; show(); } });
              c.addEventListener("contextrestored", () => { if (el._bl === out) later(() => { if (build(el, true)) show(); }, true); });
              out.push(c);
            }
            for (let j = NL - 1; j >= 0; j--) host.insertBefore(out[j], im);   // blurriest at the bottom, each sharper step above it, the photo on top
            if (out[NL]) host.insertBefore(out[NL], im);  // the shadow right under the photo
            const old = el._bl, wasHidden = !old || el._lost;
            el._bl = out; el._bw = W; el._lost = 0;
            if (old) old.forEach((c) => c.remove());      // a rebuild swaps only once the new copies exist
            if (wasHidden && el._h === 0 && el._b > 1) el._v = 0;   // it was hidden while blurred → fade in
          } catch (e) { if (!el._bl) el._bx = 1; }
          return true;
        };
        // scheduling: a garment's copies are built as soon as its photo is decoded — the focus and
        // its neighbours first — but only while nothing moves (a build never lands mid-swipe);
        // the rest follow 40ms apart.
        const lq = [];
        let lt = 0;
        let lastMB = 0, lastOff = 0;                       // the last build allowed while the carousel moves
        const drain = () => {
          lt = 0;
          if (moving || down) {
            // a garment that is (or is about to be) a neighbour and still has no copies builds anyway —
            // the one ahead in the direction of travel first, one every 100ms (a build costs ~10ms) —
            // so it never stays invisible mid-scroll; everything else waits for rest
            const now = performance.now(), head = tgt - curOff, moved = curOff - lastOff;
            lastOff = curOff;
            if (now - lastMB > 100) {
              const near = Math.round(-curOff / SP), dir = (Math.abs(head) > 1 ? head : moved) > 0 ? -1 : 1;
              for (const k of [dir, -dir, 2 * dir, 3 * dir, -2 * dir]) {
                const el = E[mod(near + k)], f = el && el._job;
                if (f && el._rdy && !el._bl) { lastMB = now; const i = lq.indexOf(f); if (i > -1) lq.splice(i, 1); f(); break; }
              }
            }
            lt = setTimeout(drain, 40); return;
          }
          const f = lq.shift();
          if (f) f();
          if (lq.length) lt = setTimeout(drain, 40);
        };
        const later = (f, first) => {
          const i = lq.indexOf(f);
          if (i > -1) { if (!first) return; lq.splice(i, 1); }
          if (first) lq.unshift(f); else lq.push(f);
          if (!lt) lt = setTimeout(drain, first ? 0 : 40);
        };
        const kick = () => { if (lq.length && !lt) lt = setTimeout(drain, 0); };   // motion ended
        const show = () => { if (!running && paint(curOff, 0, true)) run(); };
        const ensure = (el, urgent) => {
          if (!el || (el._bl && !el._lost) || el._bx) return;
          if (el._job) { if (urgent) { if (el._rdy) later(el._job, true); else el._urg = 1; } return; }
          const im = el._im || (el._im = el.querySelector("img"));
          if (!im) { el._bx = 1; return; }
          el._urg = urgent ? 1 : 0;
          const job = el._job = () => {
            if (build(el)) { el._job = null; show(); }
            else if ((el._r = (el._r || 0) + 1) < 25) later(job);
            else { el._job = null; el._bx = 1; show(); }
          };
          // decoded off the main thread first, so drawing it into the canvas costs no decode
          const ready = () => (im.decode ? im.decode().catch(() => {}) : Promise.resolve()).then(() => {
            el._rdy = 1;
            if (el._urg && !moving && !down) job();            // neighbours: right away unless the carousel moves
            else later(job, el._urg);
          });
          if (im.complete && im.naturalWidth) ready();
          else im.addEventListener("load", ready, { once: true });   // a failed photo (crossorigin retry) re-fires load
        };
        let rT = 0;
        window.addEventListener("resize", () => {
          clearTimeout(rT);
          rT = setTimeout(() => {
            const k = (window.innerHeight * GAP) / SP;
            SP *= k; loc *= k; prv *= k; tgt *= k; curOff *= k;   // keep the exact same garment in focus
            E.forEach((el) => {
              // the address bar sliding changes the height a little: KEEP the copies (they scale);
              // only a real size change (rotation) rebuilds them, swapped in once the new ones exist
              const im = el._im;
              if (el._bl && im && el._bw && Math.abs(im.offsetWidth - el._bw) / el._bw > 0.2) later(() => { if (build(el, true)) show(); });
            });
            if (!running) paint(curOff, 0, true);
          }, 200);
        }, { passive: true });
        paint(0, 0, true);                                  // neighbours stay hidden until their copies exist
        commitFocus(0);                                     // focus ±1/±2 first
        E.forEach((el) => ensure(el, false));
        // the photos further out were lazy — parked off-screen they would only start loading when reached,
        // so their blurred copies came late. Once the first garment is in, the rest load too (nearest
        // first, low priority) and their copies are ready before they are ever needed.
        const wake = () => {
          if (E._woke) return; E._woke = 1;
          E.map((el, k) => [Math.abs(wrap(k)), el]).sort((a, b) => a[0] - b[0]).forEach(([, el]) => {
            const im = el._im || (el._im = el.querySelector("img"));
            if (im && im.loading === "lazy") { try { im.fetchPriority = "low"; } catch (e) {} im.loading = "eager"; }
          });
        };
        const im0 = E[0] && (E[0]._im || E[0].querySelector("img"));
        if (!im0 || (im0.complete && im0.naturalWidth)) wake();
        else { im0.addEventListener("load", wake, { once: true }); setTimeout(wake, 2500); }              // the rest in the background
        let P = 0;
        const warmAll = () => {
          if (P >= p) return;
          const im = E[P++].querySelector("img");
          if (im) { try { im.loading = "eager"; if (im.decode) im.decode().catch(() => {}); } catch (e) {} }
          if ("requestIdleCallback" in window) requestIdleCallback(warmAll, { timeout: 900 });
          else setTimeout(warmAll, 160);
        };
        if ("requestIdleCallback" in window) requestIdleCallback(warmAll, { timeout: 1500 });
        else setTimeout(warmAll, 900);
      }
    }
  }

  /* ---------- smooth page transition on product click ---------- */
  // Browsers with cross-document View Transitions: navigate IMMEDIATELY — the browser holds the
  // old page fully lit while the next one loads (transition runs in real time WITH the address-bar
  // load), then cross-fades. Pre-darkening the page here made the engine snapshot a half-faded
  // frame → "fade to dark, then pop" instead of a live cross-fade. Older browsers keep the fade.
  // Phones: Safari/WebView cross-doc transitions are unreliable even when advertised (seen on
  // iOS 26) → mobile ALWAYS runs the theme's own fade-out; the destination fades in via `pagein`.
  // Deterministic on every device. Desktop keeps the native cross-fade (verified beautiful).

  function homeSeedHandoff(href) {
    try {
      if (new URL(href, location.href).pathname !== "/") return;
      const hs = sessionStorage.getItem("fxHomeSeed");
      if (hs) sessionStorage.setItem("fxRoom", hs); else sessionStorage.removeItem("fxRoom");
      sessionStorage.setItem("fxRoomLock", "1");
    } catch (e) {}
  }
  const VT_NATIVE = ("onpagereveal" in window) && window.matchMedia("(min-width:721px)").matches;
  const outMs = () => {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--tm")) || 450;
    return Math.max(120, v * 0.4);
  };
  function fadeNav(href) {
    homeSeedHandoff(href);
    if (VT_NATIVE) { window.location.href = href; return; }
    // THE VISION: only the CONTENT fades on tap — the room, its motion and its grain keep
    // running untouched right up to the commit; the destination resumes the same room.
    document.body.classList.add("leaving");
    setTimeout(() => { window.location.href = href; }, outMs());
  }
  $$("a.product").forEach(a => a.addEventListener("click", (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
    e.preventDefault();
    // hand the DESTINATION's room colour to the next page (not the current page's) —
    // the product page opens already tinted correctly instead of flashing the old room
    try {
      const normHex = (v) => {
        if (!v) return null;
        v = String(v).trim().toLowerCase();
        if (/^#[0-9a-f]{3}$/.test(v)) v = "#" + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
        return /^#[0-9a-f]{6}$/.test(v) ? v : null;
      };
      let hex = normHex(a.dataset.room);
      if (!hex) {
        const t = cache.get(a.dataset.img);              // prewarmed on load/hover
        if (t && t[5]) hex = "#" + t[5].map(x => x.toString(16).padStart(2, "0")).join("");
      }
      if (!hex && a.classList.contains("active")) hex = window.__fxRawHex || null;   // mobile: the room ALREADY shows this product — use its TRUE colour
      if (!hex) hex = window.__fxRawHex || null;         // last resort: the CURRENT room — always a room colour, never grey (desktop hover has usually already recoloured to the destination anyway)
      hex = normHex(hex);
      // right colour or NOTHING — a stale value from an earlier page painted the WRONG product's room
      if (hex) sessionStorage.setItem("fxRoom", hex); else sessionStorage.removeItem("fxRoom");
      sessionStorage.setItem("fxRoomLock", "1");         // pagehide must never overwrite with a hover/stale colour
    } catch (err) {}
    fadeNav(a.getAttribute("href"));
  }));
  $$(".pdp-close").forEach(a => a.addEventListener("click", (e) => {
    e.preventDefault(); fadeNav(a.getAttribute("href"));
  }));

  /* ---------- PREWARM (WebKit/Gecko) — Chromium prerenders whole pages via speculation rules;
     Safari/Firefox get nothing, so the first finger-touch (or mouse hover) on a product or
     collection link warms the HTTP cache while the finger is still down. ---------- */
  (function () {
    if (!(window.FACADE && FACADE.prefetch)) return;
    if (window.Shopify && Shopify.designMode) return;
    if (navigator.connection && navigator.connection.saveData) return;
    if (window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports("speculationrules")) return;
    const seen = new Set();
    const warm = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!/^\/(products|collections)\//.test(href)) return;
      if (seen.has(href) || seen.size >= 14) return;
      seen.add(href);
      fetch(href, { credentials: "same-origin" }).catch(() => {});
    };
    document.addEventListener("touchstart", warm, { passive: true, capture: true });
    document.addEventListener("mouseover", warm, { passive: true, capture: true });
  })();

  /* ---------- PDP ---------- */
  const pdp = $("#pdp");
  if (pdp) {
    const product = JSON.parse(pdp.dataset.product);
    const roomDefault = pdp.dataset.roomDefault;
    INV = JSON.parse(pdp.dataset.inventory || "{}");                 // parse BEFORE first render() so the stock block has data
    try { STOCK = JSON.parse(pdp.dataset.stock || "{}"); } catch (e) { STOCK = {}; }
    // cap any Shopify image URL to a sane width so old phones never decode a full-res master
    const sizeUrl = (u, w) => u ? u.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, `_${w}x$1$2`) : u;
    const selected = {};
    product.options.forEach((name, idx) => {
      const v = product.selected_or_first_available_variant || product.variants[0];
      selected[idx] = v.options[idx];
    });

    function currentVariant() {
      return product.variants.find(v =>
        v.options.every((o, idx) => o === selected[idx])) || null;
    }
    function variantForOption(idx, value) {
      const probe = { ...selected, [idx]: value };
      return product.variants.find(v => v.options.every((o, i) => o === probe[i]))
          || product.variants.find(v => v.options[idx] === value);
    }
    function render() {
      const v = currentVariant();
      const fsrc = sizeUrl((v && v.featured_image) ? v.featured_image.src : product.featured_image, 900);
      // recolor from current variant image (or product default hex) — reuse the capped url (one download)
      setRoom({ hex: roomDefault, img: fsrc, seed: product.id });
      // (--frost var removed: nothing in the CSS consumes it, and writing it invalidated <html> styles per swap)

      const colorIdx = $("#swatches") ? +$("#swatches").dataset.optionIndex - 1 : -1;
      const sizeIdx = $("#sizes") ? +$("#sizes").dataset.optionIndex - 1 : -1;
      if (colorIdx > -1) $("#colorName").textContent = "[ " + selected[colorIdx] + " ]";
      if (sizeIdx > -1) $("#sizeName").textContent = "[ " + selected[sizeIdx] + " ]";

      $$(".swatch").forEach(s => s.classList.toggle("active", colorIdx > -1 && s.dataset.value === selected[colorIdx]));
      $$("#sizes .size").forEach(s => {
        s.classList.toggle("active", sizeIdx > -1 && s.dataset.value === selected[sizeIdx]);
        const probe = variantForOption(sizeIdx, s.dataset.value);
        s.classList.toggle("soldout", probe ? !probe.available : true);
      });

      if (v) {
        $("#variantId").value = v.id;
        $("#ctaPrice").textContent = money(v.price);
        const bp = $("#blockPrice"); if (bp) bp.textContent = money(v.price);
        $("#ctaLabel").textContent = v.available ? "ADD TO CART" : "SOLD OUT";
        $("#addToCart").disabled = !v.available;
      }
      const pdpQtyVal = $("#pdpQtyVal"), pdpQtyInput = $("#pdpQtyInput"), qtyName = $("#qtyName");
      if (pdpQtyVal && pdpQtyInput) {
        pdpQtyVal.textContent = 1;
        pdpQtyVal.classList.remove("shake");   // variant switch resets the stock warning state too
        pdpQtyInput.value = 1;
        if (qtyName) qtyName.textContent = "[ 1 ]";
      }
      const stockEl = $("#stockLine");                    // live stock for the selected variant
      if (stockEl) {
        const tracked = v && (v.id in STOCK) && STOCK[v.id] !== null;   // do we have a real number?
        const qty = tracked ? STOCK[v.id] : null;
        const low = +stockEl.dataset.low || 20, always = stockEl.dataset.always === "1";
        const inLabel = stockEl.dataset.in || "In stock", outLabel = stockEl.dataset.out || "Out of stock";
        let txt, state;
        if (!v || !v.available || (tracked && qty <= 0)) { txt = outLabel; state = "out"; }
        else if (!tracked) { txt = inLabel; state = "in"; }            // untracked → no number exists
        else if (qty > low) { txt = always ? (qty + " in stock") : inLabel; state = "in"; }   // healthy
        else { txt = "Only " + qty + " left"; state = "low"; }         // at/below threshold → urgency
        stockEl.textContent = txt;
        stockEl.dataset.state = state;
        stockEl.hidden = false;
      }
      if (window.syncToImage) window.syncToImage(fsrc);   // realign the carousel + neighbour peeks to the variant image
    }
    $("#swatches") && $$(".swatch", $("#swatches")).forEach(el =>
      el.addEventListener("click", () => { selected[+$("#swatches").dataset.optionIndex - 1] = el.dataset.value; render(); }));
    $("#sizes") && $$(".size", $("#sizes")).forEach(el =>
      el.addEventListener("click", () => {
        if (el.classList.contains("soldout")) return;
        selected[+$("#sizes").dataset.optionIndex - 1] = el.dataset.value; render();
      }));
    render();

    // collapsible description — only show the toggle if it actually overflows
    const pDesc = $("#pDesc"), descToggle = $("#descToggle");
    if (pDesc && descToggle) {
      if (pDesc.scrollHeight > pDesc.clientHeight + 4) {
        descToggle.hidden = false;
        descToggle.addEventListener("click", () => {
          const nowOpen = pDesc.classList.toggle("open");
          pDesc.classList.toggle("clamp", !nowOpen);
          descToggle.textContent = nowOpen ? "READ LESS ▴" : "READ MORE ▾";
        });
      } else {
        pDesc.classList.remove("clamp");
      }
    }

    // Product Gallery Logic
    let currentImgIdx = 0;
    function initGallery() {
      const gallery = $("#gallery"), dots = $("#dots");
      const imgEls = gallery ? $$(".thumb", gallery) : (dots ? $$(".dot", dots) : []);
      const imgUrls = imgEls.map(t => t.dataset.src);
      
      const film = $("#pdpFilm");
      const layers = film ? $$(".film-img", film) : [];
      const ensureSrc = (el) => { const im = el && el.querySelector("img"); if (im && !im.getAttribute("src") && im.dataset.src) im.src = im.dataset.src; };

      // assign prev / active / next to the persistent layers — CSS transitions each one between positions
      function applyClasses() {
        const n = imgUrls.length;
        layers.forEach((el, k) => {
          const isActive = k === currentImgIdx;
          const isPrev   = n > 2 && k === (currentImgIdx - 1 + n) % n;
          const isNext   = n > 1 && k === (currentImgIdx + 1) % n;
          el.classList.toggle("active", isActive);
          el.classList.toggle("prev", isPrev);
          el.classList.toggle("next", isNext);
          if (isActive || isPrev || isNext) ensureSrc(el);     // lazy-load only the visible neighbours
        });
        gallery && $$(".thumb", gallery).forEach((t, i) => t.classList.toggle("active", i === currentImgIdx));
        dots && $$(".dot", dots).forEach((t, i) => t.classList.toggle("active", i === currentImgIdx));
      }

      window.showImage = (idx) => {
        if (!imgUrls.length) return;
        currentImgIdx = (idx + imgUrls.length) % imgUrls.length;
        applyClasses();
        const url = imgUrls[currentImgIdx];
        setRoom({ hex: roomDefault, img: url, seed: product.id });
      };
      // align the carousel to a given image url (used when a variant swaps the featured image)
      window.syncToImage = (url) => {
        const base = (u) => (u || "").replace(/_\d+x(\.\w+)(\?|$)/i, "$1$2").split("?")[0];
        const i = imgUrls.findIndex(u => base(u) === base(url));
        if (i >= 0) { currentImgIdx = i; applyClasses(); }
      };
      applyClasses();   // seed active + neighbours on load

      gallery && $$(".thumb", gallery).forEach((t, i) => t.addEventListener("click", () => window.showImage(i)));
      dots && $$(".dot", dots).forEach((t, i) => t.addEventListener("click", () => window.showImage(i)));

      // desktop scroll: UP always loops the images; DOWN advances and, at the last image, raises the sections as glass cards
      if (window.matchMedia("(min-width:721px)").matches) {
        const n = imgUrls.length;
        // lift the details panel + CTA OUT of #pdp (z-index:5 stacking context) so they stay ABOVE the rising cards
        const infoEl = $(".pdp-info"), formEl = $("#addForm"), panelEl = $(".panel"), ctaEl = $(".cta");
        if (infoEl && infoEl.parentElement !== document.body) document.body.appendChild(infoEl);
        if (formEl && formEl.parentElement !== document.body) document.body.appendChild(formEl);
        if (panelEl) panelEl.style.zIndex = "28";          // desktop — safely above the rising cards (capped at 26), below size-guide(30)/cart(40)
        if (ctaEl) ctaEl.style.zIndex = "28";
        const pdpSection = $("#pdp") ? $("#pdp").closest(".shopify-section") : null;
        const sheets = [];
        if (pdpSection) {
          let sib = pdpSection.nextElementSibling;
          while (sib) {
            if (sib.classList && sib.classList.contains("shopify-section") && sib.id !== "shopify-section-footer") sheets.push(sib);
            sib = sib.nextElementSibling;
          }
        }
        sheets.forEach((sec, i) => {
          if (sec.dataset.deskInit) return; sec.dataset.deskInit = "1";
          sec.classList.add("pdp-sheet");
          sec.style.transition = "none"; void sec.offsetWidth; sec.style.transition = "";   // land hidden INSTANTLY on load (no slide-in flash)
          sec.style.zIndex = String(Math.min(8 + i, 26));   // never above the CTA/panel(28)
          const sc = document.createElement("div"); sc.className = "pdp-sheet-scroll";
          while (sec.firstChild) sc.appendChild(sec.firstChild);
          sec.appendChild(sc); sec._scroll = sc;
          const cw = sec.querySelector(".content-wrap");          // kill the section's inline "Top spacing" so text sits at the top
          if (cw) { cw.style.margin = "0"; cw.style.marginTop = "0"; cw.style.padding = "0"; }
        });
        const hasSheets = sheets.length > 0;
        let secIdx = -1;                                          // -1 = browsing images; 0..k = which section card is on top

        let cool = false;
        const fireOnce = () => { if (cool) return false; cool = true; setTimeout(() => (cool = false), 460); return true; };  // 1 step per fling (works for mouse + trackpad)
        const showSheet = (i) => sheets[i].classList.add("up");
        const hideSheet = (i) => { sheets[i].classList.remove("up"); if (sheets[i]._scroll) sheets[i]._scroll.scrollTop = 0; };
        // expose a "jump straight to this card" hook (used when the star rating is clicked)
        window.pdpSheets = sheets;
        window.pdpRevealTo = (idx) => { if (idx < 0 || idx >= sheets.length) return; for (let i = 0; i <= idx; i++) showSheet(i); secIdx = Math.max(secIdx, idx); };

        const goDown = () => {
          if (secIdx >= 0) { if (secIdx + 1 < sheets.length) { secIdx++; showSheet(secIdx); } return; }  // next section rises on top
          if (n > 1 && currentImgIdx < n - 1) window.showImage(currentImgIdx + 1);     // next image
          else if (hasSheets) { secIdx = 0; showSheet(0); }                            // last image frozen → first section rises
          else if (n > 1) window.showImage(currentImgIdx + 1);                         // nothing below → loop
        };
        const goUp = () => {
          if (secIdx >= 0) { hideSheet(secIdx); secIdx--; return; }                    // drop the top card → back toward product
          if (n > 1) window.showImage(currentImgIdx - 1);                              // images ALWAYS loop upward
        };

        window.addEventListener("wheel", (e) => {
          if (!window.matchMedia("(min-width:721px)").matches) return;
          if (document.documentElement.classList.contains("cart-open")) return;
          if (e.target.closest(".size-guide, .cart-drawer")) return;
          const dir = e.deltaY > 0 ? 1 : -1;

          // only YIELD to an inner scroller when it is genuinely overflowing AND still has room left to scroll
          if (secIdx >= 0) {
            const sc = sheets[secIdx]._scroll;
            if (sc.scrollHeight > sc.clientHeight + 2) {
              const atB = Math.ceil(sc.scrollTop + sc.clientHeight) >= sc.scrollHeight - 4, atT = sc.scrollTop <= 0;
              if (dir > 0 && !atB) return;
              if (dir < 0 && !atT) return;
            }
          } else {
            const ps = e.target.closest(".panel-scroll");
            if (ps && ps.scrollHeight > ps.clientHeight + 2) {
              const atB = Math.ceil(ps.scrollTop + ps.clientHeight) >= ps.scrollHeight - 2, atT = ps.scrollTop <= 0;
              if ((dir > 0 && !atB) || (dir < 0 && !atT)) return;
            }
          }

          e.preventDefault();
          if (!fireOnce()) return;
          (dir > 0 ? goDown : goUp)();
        }, { passive: false, signal: sig });

        window.addEventListener("keydown", (e) => {
          if (!window.matchMedia("(min-width:721px)").matches) return;
          if (e.key === "Escape" && secIdx >= 0) { while (secIdx >= 0) { hideSheet(secIdx); secIdx--; } return; }
          if ((e.key === "ArrowDown" || e.key === "ArrowRight") && fireOnce()) goDown();
          if ((e.key === "ArrowUp"   || e.key === "ArrowLeft")  && fireOnce()) goUp();
        }, { signal: sig });
      }

      // mobile horizontal swipe
      const scrollContainer = $("#pdp") || $("#pdpStage");
      if (scrollContainer && imgUrls.length > 1) {
        let sx = 0, sy = 0;
        scrollContainer.addEventListener("touchstart", (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
        scrollContainer.addEventListener("touchend", (e) => {
          if (e.target.closest('.panel')) return;
          const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) window.showImage(currentImgIdx + (dx < 0 ? 1 : -1));
        }, { passive: true });
      }
    }

    // JS Bottom Sheet Logic
    function initBottomSheet() {
      const scrollContainer = $("#pdp") || $("#pdpStage");
      const sheet = $(".pdp-info");
      const panel = $(".panel");
      const pScroll = $(".panel-scroll");
      if (!sheet || !panel || !pScroll || !scrollContainer) return;

      let openY = 0;
      let closedY = 150;
      let isOpen = false;
      let bStartY = 0, bStartX = 0;
      let currentY = 0;
      let isDragging = false;
      let hasStack = false;
      let currentStackIdx = -1;
      let stackSecs = [];
      let openNext = () => {};
      let closeCurrent = () => {};
      let closeAll = () => {};
      let isAtPanelBottom = () => false;
      let isAtSecBottom = () => false;
      let stackCooldown = false;
      let setCooldown = () => { stackCooldown = true; setTimeout(() => { stackCooldown = false; }, 800); };

      // progressive (scroll-coupled) layer dragging — assigned in the stack block below
      let stackReveal = () => false;   // begin revealing the next layer from a touch-y
      let stackHide   = () => false;   // begin hiding the current top layer
      let stackRubber = () => false;   // begin the rubber-band on the last layer
      let stackMove   = () => {};      // follow the finger
      let stackEnd    = () => {};      // release: commit / fall back / spring
      let stackActive = () => false;   // is a layer-drag currently in progress?
      let canRevealNext = () => false; // is there a next layer to reveal?
      let forgetHidden = () => {};     // v149: a next card out of sight stops counting as "up"
      // wheel: ONE layer per fling. Stay locked through the whole momentum tail of a fling; only
      // re-arm once the wheel has essentially stopped (tiny delta) or after a long quiet gap.
      let wheelLock = false, wheelTimer = null;
      const wheelGate = (dy) => {
        if (Math.abs(dy) < 4) wheelLock = false;                 // momentum died → ready for the next fling
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => { wheelLock = false; }, 600);
        if (wheelLock) return false;
        wheelLock = true; return true;
      };

      let metricsFresh = false;   // cache the getComputedStyle read — it forced a style pass at EVERY touchstart
      const updateMetrics = () => {
        if (metricsFresh) return;
        const style = getComputedStyle($("#pdpStage"));
        openY = parseInt(style.getPropertyValue('--pdp-open-y')) || 0;
        metricsFresh = true;
      };
      // v148: the browser's bar showing / hiding changes the screen height. A card that was fully up stays exactly fully
      // up afterwards (it used to be left ~18px short of its top, which the code read as "not up")
      let upBefore = null, rsT = 0;
      window.addEventListener("resize", () => {
        metricsFresh = false; G = null; sheet.style.setProperty("--card-peek", peek() + "px");
        if (upBefore == null) upBefore = isOpen;
        clearTimeout(rsT);
        rsT = setTimeout(() => {
          const was = upBefore; upBefore = null;
          if (T == null || raf || isDragging) return;
          updateMetrics(); const b = bounds();
          setY(was ? b.o : Math.min(Math.max(T, b.o), b.c));
        }, 150);
      }, { passive: true, signal: sig });
      // v202 iPhone Brave/Safari: after the address bar or keyboard closes, the size can be announced once mid-animation
      // and never again. Measure again once things settle, and when the page comes back into view.
      const reG = () => { G = null; sheet.style.setProperty("--card-peek", peek() + "px");
        if (T != null && !raf && !isDragging) { const b = bounds(); if (T < b.o - 0.5 || T > b.c + 0.5) setY(Math.min(Math.max(T, b.o), b.c)); } };
      const reLater = () => { reG(); setTimeout(reG, 250); setTimeout(reG, 800); };
      if (window.visualViewport) window.visualViewport.addEventListener("resize", reLater, { passive: true, signal: sig });
      window.addEventListener("pageshow", reLater, { signal: sig });
      window.addEventListener("focus", reLater, { signal: sig });
      document.addEventListener("visibilitychange", () => { if (!document.hidden) reLater(); }, { signal: sig });

      // v136: the card rests wherever the finger leaves it. State = its TOP edge (T, px from the top of the screen);
      // null = closed at rest, where the CSS places it (translateY(100% - --card-peek)) from the very first frame.
      // v195 (measured on the founder's Android while the page loads): the screen height, the strip above Add to cart and
      // the card's height were read on EVERY finger move and every motion frame. While the page is still loading the
      // layout is never clean, so each read made the phone lay out the whole page there and then (369ms in one lift).
      // They are measured once and kept (G), and measured again only when one of them can change: a resize (rotation,
      // the browser bar), the card's size (ResizeObserver) or Add to cart's size.
      let G = null;
      const geo = () => {
        if (G) return G;
        const h = innerHeight, c = document.getElementById("addToCart"), r = c ? c.getBoundingClientRect() : null;
        return (G = { h, w: innerWidth, pk: r && r.height && r.top > h / 2 ? Math.round(h - r.top + 11) : 81, sh: sheet.offsetHeight });
      };
      const peek = () => geo().pk;
      const topClosed = () => { const g = geo(); return g.h - g.pk; };
      const bounds = () => { const g = geo(), c = g.h - g.pk; return { c, o: Math.min(c, g.h - g.sh + openY) }; };
      let T = null;
      const curTop = () => (T != null ? T : topClosed());
      const setY = (top) => {                     // place the card's top edge (drags and the motion below use this)
        if (!sheet.classList.contains("open") && top < topClosed() - 1) sheet.classList.add("open");
        T = top;
        // v144: phones pin the card by its top edge (CSS), so --sheet-y is the top itself and growing goes downward
        const g = geo(), y = g.w <= 720 ? top : top - g.h + g.sh;
        sheet.style.setProperty("--sheet-y", `${Math.round(y * 10) / 10}px`);
        isOpen = top <= bounds().o + 1;            // "open" means really at the top: only then the next cards may come up
      };
      // the card's height changes (description [+], fonts, a variant): keep its top edge where it is
      if (window.ResizeObserver) { const cta = document.getElementById("addToCart"); if (cta) new ResizeObserver(() => { G = null; }).observe(cta); }
      if (window.ResizeObserver) new ResizeObserver(() => { G = null; if (T != null && !raf && !isDragging && upBefore == null) setY(Math.max(T, bounds().o)); }).observe(sheet);   // v139: [+] grows the card DOWNWARD (top stays); [-] only moves it if its bottom would leave the screen
      let raf = 0, mode = "", V = 0, kind = "c", lastTs = 0;
      // v155 (founder's iPhone log 2026-10-12): [+] tapped while the card was still settling into its top made it climb
      // (259 -> 64): the settle chased the top spot of the now taller card. A motion keeps the top spot it started with
      // when the card grows ([+] grows downward, v139); if the card gets shorter the spot still moves down as before.
      let oLock = -Infinity;
      // v151 (measured on the founder's iPhone against the reference store, 2026-10-12): the phone's own scrolling.
      // Momentum fades 0.2% per ms (it slid 1.7-2s there; ours stopped in 0.7-1.2s at 0.35%/ms = "digital").
      // A flick into an end runs 60-80px past it and glides back without a wobble (critical spring, stiffness 170).
      // Pulled past an end, the stretch resists more the further it goes (the phone's rubber band).
      const DECEL = 0.998;
      const FK = 170, FC = 2 * Math.sqrt(170);          // spring: stiffness, damping (critical: no wobble)
      const rub = (d) => { const D = geo().h, a = Math.abs(d); return Math.sign(d) * (1 - 1 / (a * 0.55 / D + 1)) * D; };
      const stopMotion = () => { if (raf) cancelAnimationFrame(raf); raf = 0; lastTs = 0; };
      const restClosed = () => { stopMotion(); T = null; isOpen = false; sheet.classList.remove("open"); sheet.style.removeProperty("--sheet-y"); };
      const finish = () => { raf = 0; lastTs = 0; const b = bounds(); if (T == null || T >= b.c - 0.5) restClosed(); else isOpen = T <= b.o + 1; };
      const step = (ts) => {
        const dt = lastTs ? Math.min(32, ts - lastTs) : 16; lastTs = ts;
        const b0 = bounds(), b = { o: Math.max(b0.o, oLock), c: b0.c };
        if (mode === "glide") {                     // the finger's speed carries on and fades out, like a scroll
          const top = T + V * dt; V *= Math.pow(DECEL, dt);
          if (top < b.o) { mode = "spring"; kind = "o"; }
          else if (top > b.c) { mode = "spring"; kind = "c"; }
          setY(top);
          if (mode === "glide" && Math.abs(V) < 0.02) {
            if (T > b.c - 28) { mode = "spring"; kind = "c"; V = 0; }        // stopped just short of closed: close
            else if (T < b.o + 18) { mode = "spring"; kind = "o"; V = 0; }   // just short of open: open
            else { finish(); return; }
          }
        } else {                                    // spring onto fully open / fully closed, keeping the speed it had
          const to = kind === "o" ? b.o : b.c;
          let x = T, v = V * 1000;
          for (let i = 0; i < 4; i++) { const h = dt / 4000, a = -FK * (x - to) - FC * v; v += a * h; x += v * h; }
          V = v / 1000;
          if (Math.abs(x - to) < 0.4 && Math.abs(v) < 18) { setY(to); finish(); return; }
          setY(x);
        }
        raf = requestAnimationFrame(step);
      };
      const move = (m, v, k) => {
        mode = m; V = v || 0; if (k) kind = k;
        if (!raf) oLock = bounds().o;              // v155: the top spot this motion aims for (only ever moves down)
        if (T == null) T = topClosed();
        if (!raf) { lastTs = 0; raf = requestAnimationFrame(step); }
      };
      // touch events fire at 90–120Hz on phones — coalesce drag writes to ONE style write per frame
      let pendY = null, rafY = 0;
      const queueY = (y) => {
        pendY = y;
        if (!rafY) rafY = requestAnimationFrame(() => { rafY = 0; if (pendY != null) { setY(pendY); pendY = null; } });
      };
      const cancelQueuedY = () => { pendY = null; if (rafY) { cancelAnimationFrame(rafY); rafY = 0; } };
      // low-end Android: drop the card's backdrop blur only WHILE it is moving (imperceptible mid-motion, restored on settle)
      let liteT = 0;
      const liteOn  = () => { if (!DRAG_LITE) return; clearTimeout(liteT); document.documentElement.classList.add("drag-lite"); };
      const liteOff = () => { if (!DRAG_LITE) return; clearTimeout(liteT); liteT = setTimeout(() => document.documentElement.classList.remove("drag-lite"), 620); };
      const toggleOpen = (open) => {
        // If closing card but stack sections are visible, close section instead
        if (!open && currentStackIdx >= 0) {
          closeCurrent();
          return;
        }
        isOpen = open;
        if (open) { if (T == null) T = topClosed(); sheet.classList.add("open"); move("spring", 0, "o"); }
        else move("spring", 0, "c");
      };

      updateMetrics();
      if (window.innerWidth <= 720) sheet.style.transition = "none";   // every move is driven here (no CSS animation)
      sheet.style.setProperty("--card-peek", peek() + "px");
      restClosed();
      let samples = [];                           // the last ~120ms of finger positions while dragging the card
      let startedOpen = false;                    // was the card fully open when this touch began?

      scrollContainer.addEventListener("wheel", (e) => {
        if (window.innerWidth > 720) return;
        if (e.target.closest(".panel-scroll")) {
           const isScrollable = pScroll.scrollHeight > pScroll.clientHeight;
           if (isScrollable) {
              if (e.deltaY > 0 && Math.ceil(pScroll.scrollTop + pScroll.clientHeight) < pScroll.scrollHeight) return;
              if (e.deltaY < 0 && pScroll.scrollTop > 0) return;
           }
        }
        if (e.deltaY > 10 && !isOpen) { toggleOpen(true); return; }
        if (e.deltaY < -10 && isOpen && currentStackIdx < 0) { toggleOpen(false); return; }
        // card open & sitting at its bottom → bring up the first layer (one notch per fling)
        if (isOpen && e.deltaY > 8 && hasStack && currentStackIdx < 0 && canRevealNext() && isAtPanelBottom()) {
          if (wheelGate(e.deltaY)) { if (stackSecs[0]) stackSecs[0].style.transition = 'transform 0.5s cubic-bezier(0.22,0.68,0.3,1)'; openNext(); }
        }
      }, { passive: true });

      // v154 (founder, iPhone + Android): a quick second pull caught the card in its bounce past the top and turned into
      // bringing up the next card; the card was left there, pinned above its place, until a later drag. Whenever a touch
      // ends up not dragging this card, a card stopped outside its two ends finishes settling.
      const settleIfOut = () => { if (T == null || raf || isDragging) return; const b = bounds(); if (T < b.o - 0.5 || T > b.c + 0.5) move("glide", 0); };

      // v157c (founder): a pinch is a photo zoom, never a card pull. The card followed the first finger of a pinch
      // (touches[0]); now a second finger makes the card let go at once.
      const letGo = () => { if (!isDragging) return; isDragging = false; sheet.classList.remove("dragging"); cancelQueuedY(); liteOff(); samples = []; settleIfOut(); };
      scrollContainer.addEventListener("touchstart", (e) => {
        if (e.touches.length > 1) { letGo(); return; }
        forgetHidden();                        // v149: a finger never goes to a next card that is out of sight
        if (e.target.closest(".panel-scroll")) {
          const atBottom = Math.ceil(pScroll.scrollTop + pScroll.clientHeight) >= pScroll.scrollHeight - 2;
          if (pScroll.scrollTop > 0 && !atBottom) return;   // mid-scroll: let the panel scroll itself
        }
        updateMetrics();
        bStartY = e.touches[0].clientY;
        bStartX = e.touches[0].clientX;
        stopMotion();
        currentY = curTop();                     // the card's top edge, wherever it is (also mid-glide)
        samples = [{ y: bStartY, t: e.timeStamp }];
        startedOpen = isOpen;
        isDragging = true;
        sheet.classList.add("dragging");
      }, { passive: true });

      scrollContainer.addEventListener("touchmove", (e) => {
        if (stackActive()) { stackMove(e.touches[0].clientY); if (e.cancelable) e.preventDefault(); return; }
        if (!isDragging) return;
        if (e.touches.length > 1) { letGo(); return; }
        const cy = e.touches[0].clientY;
        const deltaY = cy - bStartY;
        if (currentStackIdx >= 0) {             // a next card is up: the product card stays put, the finger moves that card
          if (Math.abs(deltaY) > 4) {
            isDragging = false; sheet.classList.remove("dragging");
            const up = stackSecs[currentStackIdx], fullyUp = up._y != null && up._y <= 1;
            if (!fullyUp || deltaY > 0) { if (stackHide(bStartY)) stackMove(cy); }   // part-way: it follows the finger both ways (v141)
            else if (canRevealNext() && isAtSecBottom(up)) { if (stackReveal(bStartY)) stackMove(cy); }
            else if (stackRubber(bStartY)) stackMove(cy);
            settleIfOut();
          }
          if (e.cancelable) e.preventDefault();
          return;
        }
        // horizontal swipe (changing product images) while the card is closed → DON'T drag the card up
        // v148: only for a finger that started on the photos; a finger on the card always moves the card, even if it
        // drifts sideways first (thumbs do; with the card a few px off its top this used to drop every drag = "freeze")
        if (!isOpen && !e.target.closest(".panel") && Math.abs(e.touches[0].clientX - bStartX) > Math.abs(deltaY) && Math.abs(e.touches[0].clientX - bStartX) > 10) {
          isDragging = false; sheet.classList.remove("dragging"); settleIfOut(); return;
        }

        if (isOpen && e.target.closest(".panel-scroll")) {
          const isScrollable = pScroll.scrollHeight > pScroll.clientHeight;
          const notBottom = Math.ceil(pScroll.scrollTop + pScroll.clientHeight) < pScroll.scrollHeight - 2;
          if (deltaY < 0 && isScrollable && notBottom) { isDragging = false; sheet.classList.remove("dragging"); settleIfOut(); return; }
          if (deltaY > 0 && pScroll.scrollTop > 0) { isDragging = false; sheet.classList.remove("dragging"); settleIfOut(); return; }
        }

        // pulling UP at the card's bottom → start revealing the first layer (scroll-coupled)
        if (startedOpen && isOpen && deltaY < 0 && hasStack && currentStackIdx < 0 && canRevealNext() && isAtPanelBottom()) {
          if (stackReveal(cy)) { isDragging = false; sheet.classList.remove("dragging"); stackMove(cy); settleIfOut(); if (e.cancelable) e.preventDefault(); return; }
        }

        if (e.cancelable) e.preventDefault();
        liteOn();                                     // only once a real drag is in motion (never on a plain tap)
        samples.push({ y: cy, t: e.timeStamp }); while (samples.length > 2 && e.timeStamp - samples[0].t > 120) samples.shift();
        let newY = currentY + deltaY;
        if (newY < topClosed() - 1 && !sheet.classList.contains("open")) sheet.classList.add("open");
        const b = bounds();
        if (newY < b.o) newY = hasStack ? b.o : b.o + rub(newY - b.o);
        else if (newY > b.c) newY = b.c + rub(newY - b.c);
        queueY(newY);
      }, { passive: false });

      scrollContainer.addEventListener("touchend", (e) => {
        cancelQueuedY();                       // never let a stale drag frame land after the settle position
        liteOff();
        if (stackActive()) { stackEnd(); settleIfOut(); return; }
        if (!isDragging) { settleIfOut(); return; }
        isDragging = false;
        sheet.classList.remove("dragging");
        const deltaY = e.changedTouches[0].clientY - bStartY;
        if (currentStackIdx >= 0) { samples = []; settleIfOut(); return; }   // a next card is up: the product card never moves
        const s0 = samples[0], s1 = samples[samples.length - 1];
        const v = s0 && s1 && s1.t > s0.t && e.timeStamp - s1.t < 100 ? (s1.y - s0.y) / (s1.t - s0.t) : 0;   // px per ms, + = down
        samples = [];
        if (Math.abs(deltaY) <= 5 && Math.abs(v) < 0.05) { if (T == null || T >= topClosed() - 1) restClosed(); else settleIfOut(); return; }
        let top = currentY + deltaY; const b = bounds();
        if (top < b.o) top = hasStack ? b.o : b.o + rub(top - b.o);
        else if (top > b.c) top = b.c + rub(top - b.c);
        setY(top);
        move("glide", Math.max(-4, Math.min(4, v)));
      });

      // v148: iOS can take a touch away (notification pull, text selection, a system swipe) and sends touchcancel, never
      // touchend. Finish it like a still lift, so no drag (of this card or a next card) is left hanging for the next touch.
      scrollContainer.addEventListener("touchcancel", () => {
        cancelQueuedY();
        liteOff();
        if (stackActive()) { stackEnd(); settleIfOut(); return; }
        if (!isDragging) { settleIfOut(); return; }
        isDragging = false;
        sheet.classList.remove("dragging");
        samples = [];
        if (currentStackIdx >= 0 || T == null) { settleIfOut(); return; }
        move("glide", 0);
      }, { passive: true });

      // read-only state for the test tools (no effect on behaviour)
      sheet.__dbg = () => ({ T: T == null ? null : Math.round(T * 10) / 10, isOpen, isDragging, startedOpen, idx: currentStackIdx, stack: stackActive(), raf: !!raf, rafY: !!rafY, pendY, o: Math.round(bounds().o * 10) / 10, c: bounds().c, openY, innerH: innerHeight, upBefore, layers: stackSecs.map((s) => (s._y == null ? null : Math.round(s._y))) });

      panel.addEventListener("click", (e) => {
        if (window.innerWidth > 720) return;
        if (T != null && e.target.closest(".pd-acc")) return;   // the [+] works in place on a card that is already up
        if (!isOpen && !e.target.closest(".sheet-handle")) toggleOpen(true);
      });

      const handle = $(".sheet-handle");
      if (handle) {
        handle.addEventListener("click", (e) => {
          e.stopPropagation();
          toggleOpen(!isOpen);
        });
      }

      // -- Burger stacking: sections after product slide up as glass cards --
      const pdpEl = document.querySelector('#pdp');
      const mainSec = pdpEl ? pdpEl.closest('.shopify-section') : null;
      if (mainSec && window.innerWidth <= 720) {
        const stackSections = [];
        let sib = mainSec.nextElementSibling;
        while (sib) {
          if (sib.classList && sib.classList.contains('shopify-section') && sib.id !== 'shopify-section-footer') {
            stackSections.push(sib);
          }
          sib = sib.nextElementSibling;
        }

        if (stackSections.length) {
          hasStack = true;
          stackSecs = stackSections;

          // Move CTA button out of .pdp so it stays above stacked sections
          const addForm = document.getElementById('addForm');
          if (addForm) {
            document.body.appendChild(addForm);
            addForm.style.zIndex = '28';
            const ctaBtn = addForm.querySelector('.cta');           // the mobile .cta is ITSELF position:fixed → must set z-index on the button
            if (ctaBtn) ctaBtn.style.zIndex = '28';
          }

          // Style each section as a hidden fixed glass sheet with a CACHED frost (.glass-sheet ::before).
          // Content goes into an inner scroller so the frosted backdrop never scrolls/recomputes.
          stackSections.forEach((sec, i) => {
            if (sec.dataset.stackInit) return;   // guard: don't re-wrap / re-bind on shopify:section:load
            sec.dataset.stackInit = '1';
            const s = sec.style;
            s.position = 'fixed';
            s.left = '14px';
            s.right = '14px';
            s.top = 'calc(var(--top-safe) + var(--card-y,0px) + var(--this-card-y,0px))';   // global slider + per-section nudge
            // v156 (founder): its bottom sits on the same line as the product card's bottom when fully up, just above Add
            // to cart (was a fixed 92px: a 22px gap where the product card peeked out under it). Same setting: --pdp-open-y.
            s.bottom = Math.max(0, -openY) + 'px';
            s.zIndex = String(Math.min(7 + i, 26));   // never above the CTA(28)
            s.overflow = 'hidden';                 // clip the blurred ::before to the rounded corners
            s.background = 'var(--chrome)';        // real backdrop-filter glass (same as desktop) is applied via CSS
            s.border = '1px solid var(--chrome-line)';
            s.borderRadius = '22px';               // fully rounded (all corners), matches desktop
            s.boxShadow = '0 30px 70px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.4)';
            // v152 (measured on the founder's Android, Snapdragon 730G): when the card itself moved, its frosted glass and
            // shadows were redrawn every frame (16 fps). Now a plain wrapper moves it, like the product card (58 fps); the
            // card's glass, corners and shadows are never touched. Hidden position as before, on the wrapper.
            const mover = document.createElement('div');
            mover.className = 'sheet-mover';
            mover.style.cssText = 'position:fixed;left:0;top:0;width:100%;height:100%;pointer-events:none;will-change:transform;z-index:' + s.zIndex;
            sec.parentNode.insertBefore(mover, sec);
            mover.appendChild(sec);
            s.pointerEvents = 'auto';
            sec._mover = mover;
            mover.style.transform = 'translateY(' + (sec.offsetHeight + 120) + 'px)';
            void mover.offsetWidth;                                // apply the hidden position WITHOUT a transition → no slide-in flash on load
            mover.style.transition = 'transform 0.5s cubic-bezier(0.22, 0.68, 0.3, 1)';
            sec.classList.add('glass-sheet');
            // per-section blur amount (a [data-glass-blur] is emitted by our sections; app blocks use the default)
            const blurEl = sec.querySelector('[data-glass-blur]');
            if (blurEl) s.setProperty('--sec-blur', blurEl.dataset.glassBlur + 'px');

            // move the section's content into a scroller that sits above the frost
            const scroller = document.createElement('div');
            scroller.className = 'sheet-scroll-in';
            while (sec.firstChild) scroller.appendChild(sec.firstChild);
            sec.appendChild(scroller);
            sec._scroll = scroller;

            // Handle bar (fixed at the top, does not scroll)
            const bar = document.createElement('div');
            bar.style.cssText = 'width:40px;height:4px;border-radius:2px;background:rgba(255,255,255,0.5);margin:14px auto 8px auto;position:relative;z-index:1';
            sec.insertBefore(bar, scroller);
            s.display = 'flex'; s.flexDirection = 'column';      // v156: the list fills the card down to its bottom line
            bar.style.flex = '0 0 auto';
            scroller.style.flex = '1 1 auto'; scroller.style.minHeight = '0'; scroller.style.maxHeight = 'none';
            const cw = sec.querySelector('.content-wrap');
            if (cw) { cw.style.margin = '0'; cw.style.padding = '0'; }

            // v152: the outer shadows (outside the card) move to a twin right behind it: same box, same corners, same
            // values as the stylesheet gives the card. The thin inner light edges stay on the card. With the outer shadow
            // on the glass itself, the phone re-blurred the whole shadow area on every frame of movement.
            if (!DRAG_LITE) {
              const cs = getComputedStyle(sec);
              const parts = cs.boxShadow.split(/,(?![^(]*\))/).map((p) => p.trim()).filter((p) => p && p !== 'none');
              const outer = parts.filter((p) => !/inset/.test(p)).join(', '), inner = parts.filter((p) => /inset/.test(p)).join(', ');
              if (outer) {
                const twin = document.createElement('div');
                twin.className = 'sheet-shade';
                twin.setAttribute('aria-hidden', 'true');
                twin.style.cssText = 'position:absolute;pointer-events:none;box-shadow:' + outer + ';border-radius:' + cs.borderRadius;
                mover.insertBefore(twin, sec);
                sec.style.setProperty('box-shadow', inner || 'none', 'important');
                const fit = () => { const a = sec.getBoundingClientRect(), b = mover.getBoundingClientRect(); twin.style.left = (a.left - b.left) + 'px'; twin.style.top = (a.top - b.top) + 'px'; twin.style.width = a.width + 'px'; twin.style.height = a.height + 'px'; };
                fit();
                if (window.ResizeObserver) new ResizeObserver(fit).observe(sec);
                window.addEventListener('resize', fit, { passive: true });
              }
            }
          });

          // Assign functions to top-level vars so all handlers can use them
          isAtPanelBottom = () => Math.ceil(pScroll.scrollTop + pScroll.clientHeight) >= pScroll.scrollHeight - 5;
          // v176: a card's text may glide by transform (window.ySmooth, sub-pixel = no stutter): ask it where it is
          const txtTop = (sec) => { const c = sec._scroll || sec; return c.__ys ? c.__ys.get() : c.scrollTop; };
          const txtReset = (sec) => { const c = sec._scroll || sec; if (c.__ys) c.__ys.to(0, true); c.scrollTop = 0; };
          isAtSecBottom = (sec) => { const c = sec._scroll || sec; if (c.__ys) return c.__ys.get() >= c.__ys.max() - 5; return Math.ceil(c.scrollTop + c.clientHeight) >= c.scrollHeight - 5; };

          // expose a "jump straight to this card" hook (used when the star rating is clicked) — opens the panel, then stacks up to idx
          window.pdpSheets = stackSections;
          window.pdpRevealTo = (idx) => {
            if (idx < 0 || idx >= stackSections.length) return;
            toggleOpen(true);
            let i = 0;
            const step = () => { if (currentStackIdx < idx) { openNext(); if (++i < stackSections.length) setTimeout(step, 180); } };
            setTimeout(step, 120);
          };


          // v137: the next cards glide like the product card. Each rests wherever the finger leaves it (sec._y: 0 = fully
          // up, null = hidden), keeps the finger's speed on release, and springs only at the ends. No CSS animation.
          const SETTLE = 'transform 0.5s cubic-bezier(0.22,0.68,0.3,1)';
          const SPRING = 'transform 0.55s cubic-bezier(0.34,1.45,0.5,1)';
          const mv = (sec) => sec._mover || sec;            // v152: the element that moves
          const hidPx = (sec) => sec.offsetHeight + 120;    // px the section is pushed down when hidden
          const HIDDEN = (sec) => 'translateY(' + hidPx(sec) + 'px)';
          const layerY = (sec) => (sec._y != null ? sec._y : hidPx(sec));
          // v141: true when the card's top would sit less than 56px above Add to cart (hidden behind it)
          const lowVis = (sec, y) => { const c = document.getElementById("addToCart"), b = c ? c.getBoundingClientRect().top : innerHeight - 81;
            return sec.getBoundingClientRect().top - (sec._y != null ? sec._y : 0) + y > b - 56; };
          // v149 (founder's iPhone log 2026-10-12): a card pushed down plays a short settle below the screen and only
          // counted as gone when it ended (~0.5s). A drag on the product card in that time went to the invisible card
          // and restarted its settle, so quick repeated drags never reached the product card ("frozen" until a pause,
          // a tap or a refresh). Now a card counts as gone the moment it is out of sight, and touches follow what is seen.
          const ctaTop = () => { const c = document.getElementById("addToCart"), r = c ? c.getBoundingClientRect() : null; return r && r.height && r.top > innerHeight / 2 ? r.top : innerHeight; };
          const goneAt = (sec, y) => sec.getBoundingClientRect().top - (sec._y != null ? sec._y : hidPx(sec)) + y > ctaTop() - 8;
          const hideNow = (sec) => { if (sec._raf) { cancelAnimationFrame(sec._raf); sec._raf = 0; } sec._y = null; mv(sec).style.transition = 'none'; mv(sec).style.transform = HIDDEN(sec); txtReset(sec); };
          forgetHidden = () => {
            let any = false;
            stackSections.forEach((sec) => {
              if (sec._y == null || (drag && drag.sec === sec)) return;
              if (goneAt(sec, sec._y)) { hideNow(sec); any = true; }
              else if (sec._y < 0 && !sec._raf) runLayer(sec, sec._y, 0);   // v153: never left stretched past its top
            });
            if (any) syncIdx();
          };
          const putLayer = (sec, y) => { sec._y = y; mv(sec).style.transition = 'none'; mv(sec).style.transform = `translateY(${Math.round(y * 10) / 10}px)`; };
          const syncIdx = () => { let k = -1; stackSections.forEach((s, j) => { if (s._y != null && s._y < hidPx(s) - 0.5) k = j; }); currentStackIdx = k; };
          const layerRest = (sec) => {
            if (sec._y == null || sec._y >= hidPx(sec) - 0.5) { sec._y = null; mv(sec).style.transition = 'none'; mv(sec).style.transform = HIDDEN(sec); txtReset(sec); }
            syncIdx();
          };
          const runLayer = (sec, from, v, target) => {        // target undefined: glide with v; 0 or 'hid': spring there
            if (sec._raf) cancelAnimationFrame(sec._raf);
            let y = from, V = v || 0, last = 0, mode = target === undefined ? 'glide' : 'spring', to = target === 'hid' ? hidPx(sec) : 0;
            const K = FK, C = FC;                         // v151: same feel as the product card
            const step = (ts) => {
              const dt = last ? Math.min(32, ts - last) : 16; last = ts;
              const hid = hidPx(sec);
              if (target === 'hid') to = hid;
              if (V >= 0 && (mode === 'glide' || to === hid) && goneAt(sec, y)) { hideNow(sec); layerRest(sec); return; }
              if (mode === 'glide') {
                y += V * dt; V *= Math.pow(DECEL, dt);
                if (y < 0) { mode = 'spring'; to = 0; } else if (y > hid) { mode = 'spring'; to = hid; }
                if (mode === 'glide' && Math.abs(V) < 0.02) {
                  if (y > hid - 28 || lowVis(sec, y)) { mode = 'spring'; to = hid; V = 0; }
                  else if (y < 18) { mode = 'spring'; to = 0; V = 0; }
                  else { putLayer(sec, y); sec._raf = 0; layerRest(sec); return; }
                }
                putLayer(sec, y);
              } else {
                let x = y, vv = V * 1000;
                for (let k = 0; k < 4; k++) { const h = dt / 4000, acc = -K * (x - to) - C * vv; vv += acc * h; x += vv * h; }
                V = vv / 1000; y = x;
                if (Math.abs(x - to) < 0.4 && Math.abs(vv) < 18) { putLayer(sec, to); sec._raf = 0; layerRest(sec); return; }
                putLayer(sec, y);
              }
              sec._raf = requestAnimationFrame(step);
            };
            sec._raf = requestAnimationFrame(step);
          };

          // No cooldown inside these — callers decide whether to check cooldown
          openNext = () => {
            const next = currentStackIdx + 1;
            if (next >= stackSections.length) return;
            currentStackIdx = next;
            const sec = stackSections[next];
            runLayer(sec, layerY(sec), 0, 0);
          };

          closeCurrent = () => {
            if (currentStackIdx < 0) return;
            const sec = stackSections[currentStackIdx];
            currentStackIdx--;
            runLayer(sec, layerY(sec), 0, 'hid');
          };

          closeAll = () => {
            stackSections.forEach((sec) => { if (sec._y != null) runLayer(sec, sec._y, 0, 'hid'); });
            currentStackIdx = -1;
          };

          // ---------- the finger on a next card ----------
          let drag = null;   // { sec, mode:'reveal'|'hide'|'rubber', startY, hid, base, cur, s }

          canRevealNext = () => currentStackIdx + 1 < stackSections.length;
          stackActive = () => !!drag;

          const startDrag = (mode, sec, startY) => {
            if (sec._raf) { cancelAnimationFrame(sec._raf); sec._raf = 0; }
            mv(sec).style.transition = 'none';
            const hid = hidPx(sec);
            const base = mode === 'reveal' ? layerY(sec) : (sec._y != null ? sec._y : 0);
            drag = { sec, mode, startY, hid, base, cur: base, s: [{ y: startY, t: performance.now() }] };
            liteOn();
          };
          stackReveal = (y) => { if (!canRevealNext()) return false; startDrag('reveal', stackSections[currentStackIdx + 1], y); return true; };
          stackHide   = (y) => { if (currentStackIdx < 0) return false; startDrag('hide', stackSections[currentStackIdx], y); return true; };
          stackRubber = (y) => { if (currentStackIdx < 0) return false; startDrag('rubber', stackSections[currentStackIdx], y); return true; };

          let stackRaf = 0, stackPendT = null;   // coalesce the transform write to one per frame (drag.cur stays fresh for stackEnd)
          stackMove = (y) => {
            if (!drag) return;
            const d = y - drag.startY, now = performance.now();
            drag.s.push({ y, t: now }); while (drag.s.length > 2 && now - drag.s[0].t > 120) drag.s.shift();
            let ny = drag.mode === 'rubber' ? (d < 0 ? rub(d) : 0) : drag.base + d;
            if (drag.mode !== 'rubber') { if (ny < 0) ny = rub(ny); else if (ny > drag.hid) ny = drag.hid; }
            drag.cur = ny;
            stackPendT = ny;
            if (!stackRaf) stackRaf = requestAnimationFrame(() => {
              stackRaf = 0;
              if (drag && stackPendT != null) { putLayer(drag.sec, stackPendT); stackPendT = null; }
            });
          };

          stackEnd = () => {
            if (!drag) return;
            if (stackRaf) { cancelAnimationFrame(stackRaf); stackRaf = 0; }
            stackPendT = null;                           // never let a stale drag frame land after the release
            liteOff();
            const { sec, mode, cur, s } = drag;
            drag = null;
            const a0 = s[0], a1 = s[s.length - 1], now = performance.now();
            const v = a0 && a1 && a1.t > a0.t && now - a1.t < 100 ? (a1.y - a0.y) / (a1.t - a0.t) : 0;   // px per ms
            putLayer(sec, cur);
            if (mode !== 'reveal' && v >= 0 && goneAt(sec, cur)) { hideNow(sec); layerRest(sec); return; }   // v149: pushed out of sight = gone
            if (mode === 'rubber') { runLayer(sec, cur, v, 0); return; }
            if (mode === 'reveal') currentStackIdx = stackSections.indexOf(sec);   // it is coming into view
            runLayer(sec, cur, Math.max(-4, Math.min(4, v)));
          };

          // Auto-close all when card closes
          new MutationObserver(() => {
            if (!sheet.classList.contains('open') && currentStackIdx >= 0) closeAll();
          }).observe(sheet, { attributes: true, attributeFilter: ['class'] });

          // --- Per-layer gestures: scroll-coupled on touch, one-notch on wheel ---
          stackSections.forEach((sec, i) => {
            let sY = 0, startedUp = false, caught = false;

            sec.addEventListener('touchstart', (e) => {
              sY = e.touches[0].clientY;
              caught = !!sec._raf;
              if (sec._raf) { cancelAnimationFrame(sec._raf); sec._raf = 0; layerRest(sec); }   // a finger catches a gliding card
              startedUp = sec._y != null && sec._y <= 1;
            }, { passive: true });

            sec.addEventListener('touchmove', (e) => {
              if (i !== currentStackIdx) return;
              // v156: a drag starting on a review photo (Judge.me puts each in a <button>), a button or the sort dropdown moves
              // the card / list like anywhere else (a tap still opens them); only text fields and the review form window keep it
              if (e.target.closest('.jdgm-write-review-modal, .jdgm-modal, input, textarea')) return;
              if (stackActive()) { stackMove(e.touches[0].clientY); if (e.cancelable) e.preventDefault(); return; }

              const cy = e.touches[0].clientY, d = cy - sY;
              if (sec._y != null && sec._y > 1) {                 // resting part-way: the finger moves this card, both ways
                if (Math.abs(d) > 4) { stackHide(sY); stackMove(cy); if (e.cancelable) e.preventDefault(); }
                return;
              }
              if (d < 0 && isAtSecBottom(sec)) {                  // pull up past this section's end
                if (startedUp && canRevealNext()) stackReveal(cy); else stackRubber(cy);
                stackMove(cy); if (e.cancelable) e.preventDefault();
              } else if (d > 0 && txtTop(sec) <= 0) {   // pull down from the top → peel off
                stackHide(cy); stackMove(cy); if (e.cancelable) e.preventDefault();
              }
              // otherwise: let the section scroll its own content natively
            }, { passive: false });

            // v153 (founder's Android, 2026-10-12): a finger that caught this card on its way back (e.g. the rubber band
            // at the end of the review list) and then only scrolled the list or tapped left it stuck past its top, under
            // the header (_y -33, nothing running). When such a touch ends, the card settles by the usual rules again.
            const lift = () => {
              if (stackActive()) { stackEnd(); caught = false; return; }
              if (sec._y != null && !sec._raf && (caught || sec._y < 0)) runLayer(sec, sec._y, 0);
              caught = false;
            };
            sec.addEventListener('touchend', lift, { passive: true });
            sec.addEventListener('touchcancel', lift, { passive: true });   // v148

            sec.addEventListener('wheel', (e) => {
              if (i !== currentStackIdx) return;
              const sc = sec._scroll || sec;
              if (e.deltaY > 8 && isAtSecBottom(sec) && canRevealNext()) {
                if (wheelGate(e.deltaY)) { mv(stackSections[currentStackIdx + 1]).style.transition = SETTLE; openNext(); }
              } else if (e.deltaY < -8 && sc.scrollTop <= 0) {
                if (wheelGate(e.deltaY)) { mv(sec).style.transition = SETTLE; closeCurrent(); }
              }
            }, { passive: true });
          });
        }
      }
    }

    function initSection() {
      initGallery();
      initBottomSheet();
    }
    
    initSection();
    document.addEventListener("shopify:section:load", initSection, { signal: sig });

    // tap the star rating → jump straight to the reviews card, however many cards down it is
    document.addEventListener("click", (e) => {
      if (e.target.closest(".jdgm-form, .jdgm-form-modal, .jdgm-rev-widg, #judgeme_product_reviews, [class*='jdgm-form']")) return;
      const badge = e.target.closest(".jdgm-prev-badge, .jdgm-widget--star-rating, .jdgm--star-rating, [data-reviews-jump]");

      if (!badge || !window.pdpSheets || !window.pdpRevealTo) return;
      const idx = window.pdpSheets.findIndex((sec) =>
        sec.querySelector(".jdgm-rev-widg, #judgeme_product_reviews, [class*='jdgm-rev-widg'], [id*='judgeme'], [data-reviews-section]"));
      if (idx < 0) return;
      e.preventDefault(); e.stopPropagation();
      window.pdpRevealTo(idx);
    }, { capture: true, signal: sig });   // capture → runs before the review app's own scroll handler

    // collapsible rows (accordion blocks)
    $$(".acc-head").forEach((h) => h.addEventListener("click", () => h.closest(".acc").classList.toggle("open")));

    // put the size-guide link on the SAME row as shipping — shipping left, sizing right (rendered hidden → no load jump)
    const foot = $(".panel-foot"), sizingBtn = $("#sizingBtn");
    if (sizingBtn) {
      const sizingRow = sizingBtn.closest(".sizing-row");
      if (foot && !foot.contains(sizingBtn)) {
        foot.appendChild(sizingBtn);                 // into the shipping row, on the right
        if (sizingRow) sizingRow.remove();
      } else if (sizingRow) {
        sizingRow.style.display = "";                // no shipping block → just show it in its own row
      }
    }

    // size guide — true full-screen centered modal that blurs + freezes the page
    const sg = $("#sizeGuide");
    if (sg) {
      if (sg.parentElement !== document.body) document.body.appendChild(sg);   // out of the transformed panel → fixed works everywhere
      const openSG  = () => { sg.hidden = false; document.documentElement.classList.add("sg-open"); };
      const closeSG = () => { sg.hidden = true;  document.documentElement.classList.remove("sg-open"); };
      sizingBtn && sizingBtn.addEventListener("click", openSG);
      const sgClose = $("#sgClose"); sgClose && sgClose.addEventListener("click", closeSG);
      sg.addEventListener("click", e => { if (!e.target.closest(".sg-card")) closeSG(); });   // tap anywhere except the guide closes it
    }

    // add to cart — drawer opens instantly, line shows optimistically (capped at stock), one batched request reconciles
    const pendingAdd = {};
    let addTimer = null, ctaPrev = null, ctaTimer = null;
    function flashCta(txt) {
      const lbl = $("#ctaLabel"); if (!lbl) return;
      if (ctaPrev === null) ctaPrev = lbl.textContent;
      lbl.textContent = txt; clearTimeout(ctaTimer);
      ctaTimer = setTimeout(() => { lbl.textContent = ctaPrev; ctaPrev = null; }, 1500);
    }
    function optimisticAdd(v, id, qtyToAdd = 1) {
      const box = $("#cartItems");
      if (!v || !box) return false;
      const max = (id in INV) ? INV[id] : Infinity;
      if (box.querySelector("p")) box.innerHTML = "";                 // clear the "empty" state
      let line = box.querySelector(`.cart-line[data-variant="${id}"]`);
      if (line) {
        const q = line.querySelector(".q"), cur = +q.textContent;
        if (cur + qtyToAdd > max) { flashCta("MAX STOCK"); cartWarn(max > 0 ? `Only ${max} left in stock` : "Out of stock"); return false; }      // already at stock
        q.textContent = cur + qtyToAdd;
        line.querySelector(".cl-price").textContent = money((+line.dataset.unit || v.price) * (cur + qtyToAdd));
      } else {
        if (qtyToAdd > max) { flashCta("MAX STOCK"); return false; }
        const src = (v.featured_image && v.featured_image.src) ? v.featured_image.src : product.featured_image;
        line = document.createElement("div");
        line.className = "cart-line"; line.dataset.variant = id; line.dataset.unit = v.price;
        const optName = (n) => typeof n === "string" ? n : (n && n.name) || "";
        line.innerHTML = cartLineHTML({ img: sizeUrl(src, 200), title: product.title, price: money(v.price * qtyToAdd), qty: qtyToAdd,
          opts: (v.options || []).map((val, i) => ({ name: optName((product.options || [])[i]), value: val })) });
        box.prepend(line);
      }
      optimisticTotals();                                            // instant subtotal + count
      return true;
    }
    async function flushAdd() {
      const items = Object.entries(pendingAdd).map(([id, quantity]) => ({ id, quantity }));
      for (const k in pendingAdd) delete pendingAdd[k];
      if (!items.length) return;
      let ok = true;
      try { ok = (await fetch(SHOP.routes.cart_add, { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify({ items }) })).ok; }
      catch (e) {}
      if (!ok) flashCta("MAX STOCK");
      const cart = await fetch(SHOP.routes.cart + ".js").then(r => r.json()).catch(() => null);
      if (cart) document.dispatchEvent(new CustomEvent("cart:changed", { detail: cart }));   // reconcile drawer + page from one cart
    }
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".pdp-qty [data-act]");
      if (!btn) return;
      const valSpan = $("#pdpQtyVal"), input = $("#pdpQtyInput"), nameSpan = $("#qtyName");
      if (!valSpan || !input) return;
      let currentQty = +valSpan.textContent || 1;
      const vt = $("#variantId"), id = vt ? vt.value : null;
      const max = (id && id in INV) ? INV[id] : Infinity;
      if (btn.dataset.act === "inc") {
        if (currentQty >= max) {
          valSpan.classList.remove("shake");
          void valSpan.offsetWidth;
          valSpan.classList.add("shake");
          clearTimeout(valSpan._shakeT);   // flash the warning colour, then return to normal white
          valSpan._shakeT = setTimeout(() => valSpan.classList.remove("shake"), 1300);
          return;
        }
        currentQty++;
      } else if (btn.dataset.act === "dec") {
        if (currentQty > 1) currentQty--;
      }
      valSpan.textContent = currentQty;
      input.value = currentQty;
      if (nameSpan) nameSpan.textContent = `[ ${currentQty} ]`;
    }, { signal: sig });

    $("#addForm").addEventListener("submit", (e) => {
      e.preventDefault();
      const id = $("#variantId").value, v = currentVariant();
      const qtyInput = $("#pdpQtyInput");
      const qty = qtyInput ? +qtyInput.value || 1 : 1;
      openCart();                                                     // drawer appears instantly
      if (!optimisticAdd(v, id, qty)) return;                              // at stock cap → don't queue a server add
      pendingAdd[id] = (pendingAdd[id] || 0) + qty;
      clearTimeout(addTimer); addTimer = setTimeout(flushAdd, 280);   // batch a burst of taps into one request
    });
  }
  }   /* ======== end PAGE SCOPE (initPage) ======== */
  window.FACADE = window.FACADE || {};
  window.FACADE.initPage = initPage;
  window.FACADE.teardownPage = teardownPage;
  initPage();

  /* ---------- cart drawer ---------- */
  const drawer = $("#cartDrawer"), overlay = $("#cartOverlay");
  const EMPTY = `<p class="cart-empty">Your cart is empty</p>`;
  // one cart line: photo · title + price · options (size as SIZE: X) · QTY − n + · DELETE
  function escH(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]); }
  function cartLineHTML(o) {
    const opts = (o.opts || []).filter((x) => x && x.value && !/^default title$/i.test(x.value))
      .map((x) => `<span>${/size/i.test(x.name || "") ? "Size: " : ""}${escH(x.value)}</span>`).join("");
    return `<img src="${o.img || ""}" alt="" width="100" height="89" loading="lazy"><div class="cl-body"><div class="cl-info">`
      + `<div class="cl-top"><span class="cl-title">${escH(o.title)}</span><span class="cl-price">${o.price}</span></div>`
      + `<div class="cl-meta">${opts}</div></div>`
      + `<div class="cl-row"><span class="qty"><span class="ql">Qty</span>`
      + `<button type="button" data-act="dec" aria-label="Decrease quantity"><svg width="6" height="2" viewBox="0 0 6 2" fill="none" aria-hidden="true"><path d="M0 1h6" stroke="currentColor"/></svg></button>`
      + `<span class="q">${o.qty}</span>`
      + `<button type="button" data-act="inc" aria-label="Increase quantity"><svg width="6" height="6" viewBox="0 0 6 6" fill="none" aria-hidden="true"><path d="M3 0v6M0 3h6" stroke="currentColor"/></svg></button></span>`
      + `<button type="button" class="cl-del" data-act="del">Delete</button></div></div>`;
  }
  // nothing in the cart → only the title and the empty line show (summary, checkout, badges wait)
  function markEmpty(on) { const d = document.getElementById("cartDrawer"); if (d) d.classList.toggle("is-empty", !!on); }
  // ONE delegated handler → +/−/DELETE work on EVERY line including the instant optimistic one (no waiting for the server re-render)
  const cartItemsBox = $("#cartItems");
  if (cartItemsBox) cartItemsBox.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-act]"); if (!btn) return;
    const line = btn.closest(".cart-line"); if (!line) return;
    const id = line.dataset.key || line.dataset.variant;          // optimistic lines have a variant but no key yet
    if (!id) return;
    const q = +line.querySelector(".q").textContent || 0;
    if (btn.dataset.act === "inc") changeQty(id, q + 1, line);
    else if (btn.dataset.act === "dec") changeQty(id, q - 1, line);
    else if (btn.dataset.act === "del") { line.style.height = line.offsetHeight + "px"; line.offsetWidth; line.classList.add("removing"); changeQty(id, 0, line); }
  });
  function setTotals(cart) {
    if (!cart || typeof cart.total_price !== "number") return;   // ignore error responses → never "NaN"
    const cc = $("#cartCount");
    if (cc) { cc.textContent = cart.item_count; cc.hidden = cart.item_count === 0; }
    markEmpty(cart.item_count === 0);
    $("#cartSubtotal").textContent = money(cart.total_price);
  }
  // recompute subtotal + count from the lines on screen (instant; server reconciles after)
  function optimisticTotals() {
    let sub = 0, count = 0;
    $$("#cartItems .cart-line").forEach(l => {                      // drawer lines only (cart page has its own)
      if (l.classList.contains("removing")) return;
      const q = +l.querySelector(".q").textContent || 0;
      sub += (+l.dataset.unit || 0) * q; count += q;
    });
    const st = $("#cartSubtotal"); if (st) st.textContent = money(sub);
    const cc = $("#cartCount"); if (cc) { cc.textContent = count; cc.hidden = count === 0; }
    markEmpty(count === 0);
  }
  function renderCart(cart) {
    setTotals(cart);
    $("#cartItems").innerHTML = cart.items.map(it =>
      `<div class="cart-line" data-key="${it.key}" data-variant="${it.variant_id}" data-unit="${it.final_price || it.price}">`
      + cartLineHTML({ img: it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '', title: it.product_title,
                       price: money(it.final_line_price), qty: it.quantity, opts: it.options_with_values })
      + `</div>`).join("") || EMPTY;

    // buttons are handled by the delegated #cartItems listener above — no per-line binding needed (works for optimistic lines too)
  }
  async function loadCart() {
    const cart = await fetch(SHOP.routes.cart + ".js").then(r => r.json());
    renderCart(cart);
  }
  // professional "max stock" notice — drawer foot by default, cart page passes its own element
  function cartWarn(msg, sel) {
    const w = $(sel || "#cartWarn"); if (!w) return;
    w.textContent = msg; w.hidden = false;
    requestAnimationFrame(() => w.classList.add("show"));
    clearTimeout(w._t); w._t = setTimeout(() => { w.classList.remove("show"); setTimeout(() => { w.hidden = true; }, 250); }, 3200);
  }
  // optimistic qty (instant) + debounced server change; price/subtotal/cap ALL come from the server re-render
  const qWork = {};
  function changeQty(id, quantity, line) {
    line = line || document.querySelector(`#cartItems .cart-line[data-key="${id}"], #cartItems .cart-line[data-variant="${id}"]`);
    if (!line) return;
    const vid = line.dataset.variant;
    const cap = (vid && STOCK && STOCK[vid] != null) ? STOCK[vid]                  // never let the cart exceed REAL stock (even if the product allows overselling)
              : (vid && (vid in INV)) ? INV[vid] : Infinity;
    if (quantity > cap) { quantity = cap; cartWarn(cap > 0 ? `Only ${cap} left in stock` : "Out of stock"); }
    if (quantity <= 0) line.classList.add("removing");
    else {
      line.querySelector(".q").textContent = quantity;                            // instant qty
      const unit = +line.dataset.unit || 0;
      if (unit) line.querySelector(".cl-price").textContent = money(unit * quantity);   // instant line price
    }
    optimisticTotals();                                                           // instant subtotal + count (server reconciles after)
    const w = qWork[id] || (qWork[id] = {});
    w.target = quantity; w.line = line;
    clearTimeout(w.t);
    w.t = setTimeout(() => syncQty(id), 260);
  }
  async function syncQty(id) {
    const w = qWork[id];
    if (!w || w.busy) return;
    w.busy = true; const sent = w.target;
    const line = w.line || document.querySelector(`#cartItems .cart-line[data-key="${id}"], #cartItems .cart-line[data-variant="${id}"]`);
    const serverId = (line && line.dataset.key) || id;                            // use the real key once the server has assigned it, else the variant id
    let res, cart;
    try {
      res = await fetch(SHOP.routes.cart_change, {
        method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ id: serverId, quantity: sent })
      });
      cart = await res.json();
    } catch (e) { w.busy = false; return; }
    w.busy = false;
    if (w.target !== sent) { clearTimeout(w.t); w.t = setTimeout(() => syncQty(id), 50); return; }   // newer taps → re-sync, don't reconcile yet
    delete qWork[id];
    if (!res.ok || !cart || !Array.isArray(cart.items)) {
      try {
        const realCart = await fetch(SHOP.routes.cart + ".js").then(r => r.json());
        applyCart(realCart);
        document.dispatchEvent(new CustomEvent("cart:changed", { detail: realCart }));
      } catch(e) { loadCart(); }
      return;
    }
    applyCart(cart);                                                 // reconcile from THIS response (post-change truth, never stale)
    document.dispatchEvent(new CustomEvent("cart:changed", { detail: cart }));   // hand the same cart to the page
  }
  // patch the drawer in place from a cart JSON; never clobber a line that's mid-tap
  function applyCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    setTotals(cart);                                                 // subtotal + badge from the server
    const box = $("#cartItems"); if (!box) return;
    if (!cart.items.length) { box.innerHTML = EMPTY; return; }       // emptied → instant, no reload
    const lines = $$("#cartItems .cart-line");
    lines.forEach(line => {                                          // give optimistic lines (no key yet) their real key by variant → patch in place, no rebuild flash
      if (!line.dataset.key && line.dataset.variant) {
        const it = cart.items.find(i => String(i.variant_id) === line.dataset.variant);
        if (it) line.dataset.key = it.key;
      }
    });
    const domKeys = lines.map(l => l.dataset.key), cartKeys = cart.items.map(i => i.key);
    const sameSet = domKeys.length === cartKeys.length && domKeys.every(k => cartKeys.indexOf(k) >= 0);
    if (!sameSet) { renderCart(cart); return; }                      // a line added/removed → rebuild from THIS cart (no extra fetch)
    lines.forEach(line => {
      const item = cart.items.find(i => i.key === line.dataset.key);
      if (!item) return;
      line.dataset.unit = item.final_price;                         // keep the per-unit price fresh for optimistic math
      const busy = qWork[line.dataset.key] || qWork[line.dataset.variant];
      if (!busy) {
        const uiQty = +line.querySelector(".q").textContent;
        if (uiQty > item.quantity) cartWarn(item.quantity > 0 ? `Only ${item.quantity} left in stock` : "Out of stock");
        line.querySelector(".q").textContent = item.quantity;   // settled → snap to the SERVER quantity (clamps an over-stock drawer back to real)
      }
      line.querySelector(".cl-price").textContent = money(item.final_line_price);
    });
  }
  document.addEventListener("cart:changed", (e) => {                  // change anywhere → drawer reflects it
    if (e && e.detail && Array.isArray(e.detail.items)) applyCart(e.detail);   // reconcile from the handed JSON (no fetch)
    else if (drawer) loadCart();
  });
  function openCart() { if (!drawer) return; document.documentElement.classList.add("cart-open"); overlay.classList.add("open"); drawer.classList.add("open"); }
  function closeCart() { if (!drawer) return; document.documentElement.classList.remove("cart-open"); overlay.classList.remove("open"); drawer.classList.remove("open"); }
  $("#cartClose") && $("#cartClose").addEventListener("click", closeCart);
  document.addEventListener("keydown", (e) => { if (e.key === "Escape" && drawer && drawer.classList.contains("open")) closeCart(); });
  overlay && overlay.addEventListener("click", closeCart);
  $$("[data-cart-toggle]").forEach(el => el.addEventListener("click", () => { openCart(); loadCart(); }));
  if (drawer) loadCart();   // preload existing items into the (hidden) drawer so they show instantly on open

})();
